import { useState, useCallback } from 'react';
import { PackageOrder, TrendyolOrder } from '../types';
import { calculateTrendyolSellerDiscount } from '../utils/helpers';
import type { SellerSnapshot } from '../../../../services/sellerInfoService';
import { getPackageOrderById } from '../../../../services/salesService';
import salesService from '../../../../services/salesService';
import { WarehouseService } from '../../../accounting/inventory/services/warehouseService';
import { InventoryService } from '../../../accounting/inventory/services/inventoryService';
import { foodPlatformMappingService, FoodPlatform } from '../services/foodPlatformMappingService';
import { salesApi, trendyolApi, migrosApi, yemeksepetiApi, getirApi } from '../../../../config/apiClient';
import { pavoApiService } from '../../../../services/pavoApiService';
import { pavoSignalRService } from '../../../../services/pavoSignalRService';
// ✅ Ödeme niyeti: tarayıcı kapansa da sunucu satışı aynı işlem anahtarıyla tamamlar
import {
  createPaymentIntent,
  reportDeviceResult,
  abandonPaymentIntent,
  type PaymentIntentEndpoint,
  type PaymentIntentKind,
} from '../../../../services/paymentIntentService';
import {
  restP2PPaymentService,
  type RestP2PPaymentRequest,
  type RestP2PExternalPayment,
} from '../../../../services/restP2PPaymentService';

// ─── Stok düşümü için depo belirleme yardımcısı ───────────────────────────
// Öncelik: ekran varsayılan deposu → şubeye bağlı depo → yeni depo oluştur
async function resolveWarehouseForBranch(
  companyId: string,
  branchId: string | null,
  defaultWarehouseId: string | null,
  allWarehouses: any[]
): Promise<string | null> {
  if (defaultWarehouseId) {
    console.log('[STOK] Ekran varsayılan deposu kullanılıyor:', defaultWarehouseId);
    return defaultWarehouseId;
  }
  if (branchId) {
    const w = allWarehouses.find((wh: any) =>
      (wh.branchesId ?? wh.branchId ?? wh.BranchesId ?? wh.BranchId) === branchId
    );
    if (w) {
      const whId = w.id || w.Id;
      console.log('[STOK] Şubeye bağlı depo bulundu:', whId);
      return whId;
    }
  }
  try {
    console.log('[STOK] Depo bulunamadı, yeni oluşturuluyor...');
    const created = await WarehouseService.createWarehouse({
      companyId,
      name: 'Şube Deposu',
      isDefault: false,
    });
    console.log('[STOK] Yeni depo oluşturuldu:', created.id);
    return created.id;
  } catch (e) {
    console.error('[STOK] Depo oluşturulamadı:', e);
    return null;
  }
}

const ZERO_GUID = '00000000-0000-0000-0000-000000000000';

// ─── Platform ürünlerini kendi stoklarımızla isim bazlı eşleştirme ─────────
// Tam isim eşleşmesi varsa ProductId, WarehouseId, HasRecipe gibi alanları doldurur.
// Eşleşme yoksa item değişmeden kalır (stok düşülmez).
function safeParseBranchId(branchId: string | number | null | undefined): number | null {
  if (branchId == null) return null;
  const parsed = typeof branchId === 'number' ? branchId : parseInt(String(branchId), 10);
  return isNaN(parsed) ? null : parsed;
}

/** Platform siparişi satırını eşleştirme için işaretler (faturaya gönderilmeden önce silinir). */
interface PlatformLineTag {
  id?: string | null;
  name: string;
  variant?: string | null;
}

/** Eşleştirme için sipariş bilgisi: platform, şube, entegrasyon (marka / hesap). */
export interface PlatformResolveContext {
  platform: FoodPlatform;
  branchId: string | null;
  platformIntegrationId: number | null;
}

const MAPPED_PLATFORMS: string[] = ['yemeksepeti', 'trendyol', 'migros'];

export function platformResolveContext(order: PackageOrder | null | undefined, screenData: any): PlatformResolveContext | null {
  if (!order || !MAPPED_PLATFORMS.includes(order.source)) return null;
  return {
    platform: order.source as FoodPlatform,
    branchId: screenData?.branchId ?? null,
    platformIntegrationId: order.platformIntegrationId ?? null,
  };
}

const stripTags = (items: any[]): any[] =>
  items.map((it) => {
    if (!it || !('__platformLine' in it)) return it;
    const { __platformLine, ...rest } = it;
    void __platformLine;
    return rest;
  });

/** Ürünün hedef depoda kaydı yoksa 0 adetle açar (stok hareketi depo kaydı ister). */
async function ensureInWarehouse(companyId: string, productId: string, warehouseId: string, found?: any) {
  const foundWarehouseId: string | undefined = found?.warehouseId || found?.WarehouseId;
  const warehouseStocks: any[] = found?.warehouseStocks || found?.WarehouseStocks || [];
  const inWarehouse = found
    ? foundWarehouseId === warehouseId || warehouseStocks.some((ws: any) => (ws.warehouseId || ws.WarehouseId) === warehouseId)
    : false;
  if (inWarehouse) return;
  try {
    await InventoryService.addStockToWarehouse({ companyId, productId, warehouseId, quantity: 0 });
  } catch (e) {
    // Zaten kayıtlıysa sunucu hata dönebilir; stok düşümü yine denenir
    console.warn('[STOK] Ürün depoya eklenemedi (zaten kayıtlı olabilir):', productId, e);
  }
}

/**
 * Yemek platformu (Yemeksepeti, Uber Eats, Migros) satırlarını "Platform Ürün Eşleştirme"ye göre
 * bizim ürünlere bağlar: şubenin değişikliği, yoksa platform hesabının eşleşmesi; eşleşmesi olmayan
 * ürün için sunucu o an benzerlik hesaplar, eşiği geçerse otomatik bağlar. Eşleşmeyen satır stoğa dokunmaz.
 */
async function resolvePlatformLines(
  items: any[],
  companyId: string,
  warehouseId: string,
  ctx: PlatformResolveContext
): Promise<any[] | null> {
  const lines = items
    .map((it, index) => ({ it, index }))
    .filter(({ it }) => it?.__platformLine);
  if (lines.length === 0) return items;
  try {
    const resolved = await foodPlatformMappingService.resolve({
      platform: ctx.platform,
      branchId: ctx.branchId,
      platformIntegrationId: ctx.platformIntegrationId,
      lines: lines.map(({ it, index }) => {
        const tag: PlatformLineTag = it.__platformLine;
        return { index, externalProductId: tag.id ?? null, name: tag.name, variant: tag.variant ?? null };
      }),
    });
    const byIndex = new Map(resolved.map((r) => [r.index, r]));
    const out = [...items];
    for (const { index } of lines) {
      const r = byIndex.get(index);
      if (!r?.inventoryItemId) continue;
      await ensureInWarehouse(companyId, r.inventoryItemId, warehouseId);
      out[index] = {
        ...out[index],
        ProductId: r.inventoryItemId,
        WarehouseId: warehouseId,
        StockCode: r.stockCode || out[index].StockCode || null,
        HasRecipe: r.hasRecipe,
        HasMenu: r.hasMenu,
      };
    }
    console.log('[STOK] Platform ürün eşleştirmesi:', resolved.map((r) => `${r.index}:${r.status}${r.inventoryItemName ? `→${r.inventoryItemName}` : ''}`).join(', '));
    return out;
  } catch (e) {
    console.warn('[STOK] Platform ürün eşleştirmesi okunamadı, ad eşleşmesine düşülüyor:', e);
    return null;
  }
}

async function enrichItemsWithInventory(
  items: any[],
  companyId: string,
  resolvedWarehouseId: string | null,
  ctx?: PlatformResolveContext | null
): Promise<any[]> {
  if (!resolvedWarehouseId) return stripTags(items);

  if (ctx) {
    const mapped = await resolvePlatformLines(items, companyId, resolvedWarehouseId, ctx);
    if (mapped) return stripTags(mapped);
  }

  return stripTags(await Promise.all(
    items.map(async (item) => {
      const productName: string = item.ProductName || '';
      if (!productName) return item;
      try {
        const results = await salesService.searchProducts(productName);
        const normName = productName.toLowerCase().trim();
        const found = results.find((r: any) =>
          (r.name || r.Name || '').toLowerCase().trim() === normName
        );
        if (!found) return item;

        const productId: string = found.id || found.Id || ZERO_GUID;
        if (productId === ZERO_GUID) return item;

        // Ürünün hedef depoda kaydı var mı?
        const foundWarehouseId: string | undefined = found.warehouseId || found.WarehouseId;
        const warehouseStocks: any[] = found.warehouseStocks || found.WarehouseStocks || [];
        const inWarehouse =
          foundWarehouseId === resolvedWarehouseId ||
          warehouseStocks.some(
            (ws: any) => (ws.warehouseId || ws.WarehouseId) === resolvedWarehouseId
          );
        if (!inWarehouse) {
          try {
            await InventoryService.addStockToWarehouse({
              companyId,
              productId,
              warehouseId: resolvedWarehouseId,
              quantity: 0,
            });
            console.log('[STOK] Ürün depoya eklendi (qty=0):', productName);
          } catch (e) {
            console.warn('[STOK] Ürün depoya eklenemedi:', productName, e);
          }
        }

        return {
          ...item,
          ProductId: productId,
          WarehouseId: resolvedWarehouseId,
          StockCode: found.stockCode || found.StockCode || item.StockCode || null,
          HasRecipe: found.hasRecipe || found.HasRecipe || false,
          HasMenu: found.hasMenu || found.HasMenu || false,
        };
      } catch (e) {
        console.warn('[STOK] Ürün eşleştirme hatası:', productName, e);
        return item;
      }
    })
  ));
}

// ─── Platform indirim/promosyon kalem önekleri ─────────────────────────────
// Gün Sonu Raporu'nda hangi platformdan geldiği net görünsün ve iki ayrı
// platformdaki aynı isimli indirimler raporda ayrı satırlarda gruplansın diye.
// Idempotent: zaten önek taşıyan isimler tekrar sarılmaz.
const PLATFORM_PREFIXES: Record<string, string> = {
  yemeksepeti: 'YEMEKSEPETI',
  getir: 'GETIR',
  trendyol: 'UBER EATS',
  migros: 'MIGROS',
};

// Artik kullanilmayan ama gecmis kayitlarda duran onekler. Uber, Trendyol Go'yu
// satin alip Uber Eats'e tasidi; eski faturalarda 'TRENDYOL - ...' kalemler var.
// Bu isimler tekrar sarmalanmasin diye taninan onek listesinde kalmali.
const LEGACY_PLATFORM_PREFIXES = ['TRENDYOL'];

function platformPrefix(src: string): string {
  return PLATFORM_PREFIXES[(src || '').toLowerCase()] || 'PLATFORM';
}

function withPrefix(src: string, name: string): string {
  const p = platformPrefix(src);
  const trimmed = (name || '').trim();
  // Mevcut tüm bilinen önekleri kontrol et — örn. 'Getir Platform İndirimi'
  // 'Trendyol İndirimi' gibi eski isimleri tekrar sarmamak için.
  const knownPrefixes = [...Object.values(PLATFORM_PREFIXES), ...LEGACY_PLATFORM_PREFIXES];
  const upper = trimmed.toUpperCase();
  for (const known of knownPrefixes) {
    if (upper.startsWith(known)) return trimmed;
  }
  return `${p} - ${trimmed}`;
}

// ─── Yemeksepeti fatura kalemleri (Cash, POS, Online ortak) ────────────────
// Items + toppings + raw JSON'daki discounts[] her biri ayrı negatif kalem
// + son diff düzeltme (itemsTotal vs orderTotal). Items toplamı = orderTotal
// garantilenir. ysOrder veya items boş ise tek kalem fallback döner.
function buildYsInvoiceItems(
  ysOrder: any | undefined,
  orderTotal: number,
  shortOrderNo: string | number | null | undefined
): any[] {
  const fallback = [{
    ProductId: ZERO_GUID,
    ProductName: `Yemeksepeti Siparis #${shortOrderNo}`,
    StockCode: null,
    WarehouseId: ZERO_GUID,
    WarehouseName: null,
    Quantity: 1, Unit: 'adet', UnitPrice: orderTotal,
    VatRate: 0, SctRate: 0, PriceType: 'VAT_INCLUDED',
    HasRecipe: false, HasMenu: false,
    SelectedOptions: null, TotalPriceDifference: 0,
  }];

  const rawItems = ysOrder?.items || [];
  if (rawItems.length === 0) return fallback;

  const items: any[] = rawItems.map((item: any) => {
    const baseUnitPrice = Number(item.unitPrice || 0);
    const toppingsTotal = (item.selectedToppings || []).reduce(
      (sum: number, t: any) => sum + Number(t.price || 0),
      0
    );
    const ysName: string = item.name || 'Yemeksepeti Urun';
    return {
      __platformLine: {
        id: (item.productId ?? item.ProductId)?.toString() || null,
        name: ysName,
        variant: item.variationName ?? item.VariationName ?? null,
      } as PlatformLineTag,
      ProductId: ZERO_GUID,
      ProductName: ysName,
      StockCode: null,
      WarehouseId: ZERO_GUID,
      WarehouseName: null,
      Quantity: Number(item.quantity) || 1,
      Unit: 'adet',
      UnitPrice: baseUnitPrice + toppingsTotal,
      VatRate: 0, SctRate: 0, PriceType: 'VAT_INCLUDED',
      HasRecipe: false, HasMenu: false,
      SelectedOptions: null, TotalPriceDifference: 0,
    };
  });

  // Raw JSON'daki discounts dizisinden isimli indirim kalemleri oluştur
  try {
    const rawJson = ysOrder?.rawJsonData ?? ysOrder?.RawJsonData;
    if (rawJson) {
      const parsed = JSON.parse(rawJson);
      const discounts: { name?: string; amount?: string | number }[] = parsed?.discounts || [];
      for (const disc of discounts) {
        const discAmount = Number(disc.amount || 0);
        if (discAmount > 0) {
          items.push({
            ProductId: ZERO_GUID,
            ProductName: withPrefix('yemeksepeti', disc.name || 'Platform İndirimi'),
            StockCode: null,
            WarehouseId: ZERO_GUID, WarehouseName: null,
            Quantity: 1, Unit: 'adet',
            UnitPrice: -discAmount,
            VatRate: 0, SctRate: 0, PriceType: 'VAT_INCLUDED',
            HasRecipe: false, HasMenu: false,
            SelectedOptions: null, TotalPriceDifference: 0,
          });
        }
      }
    }
  } catch (e) {
    console.warn('[FATURA] YS rawJsonData parse hatası, genel düzeltme kullanılacak:', e);
  }

  // Garanti: Items toplamı = orderTotal (kalem isim/miktar verileri eksik olsa bile)
  const itemsTotal = Math.round(items.reduce((sum: number, it: any) => sum + (it.UnitPrice * it.Quantity), 0) * 100) / 100;
  const diff = Math.round((itemsTotal - orderTotal) * 100) / 100;
  if (Math.abs(diff) > 0.01) {
    console.log(`[FATURA][YS] Kalem toplamı (${itemsTotal.toFixed(2)}) ≠ Sipariş toplamı (${orderTotal.toFixed(2)}), fark: ${diff.toFixed(2)}`);
    items.push({
      ProductId: ZERO_GUID,
      ProductName: withPrefix('yemeksepeti', diff > 0 ? 'Platform İndirimi' : 'Fark Düzeltme'),
      StockCode: null,
      WarehouseId: ZERO_GUID, WarehouseName: null,
      Quantity: 1, Unit: 'adet',
      UnitPrice: -diff,
      VatRate: 0, SctRate: 0, PriceType: 'VAT_INCLUDED',
      HasRecipe: false, HasMenu: false,
      SelectedOptions: null, TotalPriceDifference: 0,
    });
  }

  return items;
}

// ─── Trendyol fatura kalemleri (Cash, POS, Online ortak) ───────────────────
// Lines + promotions[] her kampanya ayrı negatif kalem + son diff düzeltme.
// Items toplamı = orderTotal garantilenir.
function buildTrendyolInvoiceItems(
  trdOrder: TrendyolOrder | undefined,
  orderTotal: number,
  shortOrderNo: string | number | null | undefined
): any[] {
  const fallback = [{
    ProductId: ZERO_GUID,
    ProductName: `Uber Eats Siparis #${shortOrderNo}`,
    StockCode: null,
    WarehouseId: ZERO_GUID,
    WarehouseName: null,
    Quantity: 1, Unit: 'adet', UnitPrice: orderTotal,
    VatRate: 0, SctRate: 0, PriceType: 'VAT_INCLUDED',
    HasRecipe: false, HasMenu: false,
    SelectedOptions: null, TotalPriceDifference: 0,
  }];

  const lines = trdOrder?.lines || [];
  if (lines.length === 0) return fallback;

  const items: any[] = lines.map((line) => ({
    __platformLine: {
      id: line.productId != null && Number(line.productId) !== 0 ? String(line.productId) : null,
      name: line.name || 'Uber Eats Urun',
    } as PlatformLineTag,
    ProductId: ZERO_GUID,
    ProductName: line.name || 'Uber Eats Urun',
    StockCode: null,
    WarehouseId: ZERO_GUID,
    WarehouseName: null,
    Quantity: line.items?.length || 1,
    Unit: 'adet',
    UnitPrice: line.unitSellingPrice || line.price || 0,
    VatRate: 0, SctRate: 0, PriceType: 'VAT_INCLUDED',
    HasRecipe: false, HasMenu: false,
    SelectedOptions: null, TotalPriceDifference: 0,
  }));

  // Trendyol top-level promotions[]: birden fazla kampanya gelebilir.
  // Her birini ayrı negatif kalem olarak push'la — Pavo cihazı tarafında toplam
  // indirim itemsTotal - totalAmount olarak otomatik aggregate edilir.
  const promos = trdOrder?.promotions || [];
  let promotionTotal = 0;
  for (const promo of promos) {
    const amount = Number(promo?.totalSellerAmount || 0);
    if (amount > 0) {
      promotionTotal += amount;
      items.push({
        ProductId: ZERO_GUID,
        ProductName: withPrefix('trendyol', promo.description || 'İndirim'),
        StockCode: null,
        WarehouseId: ZERO_GUID, WarehouseName: null,
        Quantity: 1, Unit: 'adet',
        UnitPrice: -amount,
        VatRate: 0, SctRate: 0, PriceType: 'VAT_INCLUDED',
        HasRecipe: false, HasMenu: false,
        SelectedOptions: null, TotalPriceDifference: 0,
      });
    }
  }

  // Kupon (ör. "Plus Yemek Kuponu") promotions[] disinda coupon alaninda gelir;
  // ayri kalem olarak yazilmazsa asagidaki diff duzeltmesine "Platform İndirimi"
  // adiyla dusuyor ve faturada kuponun adi kayboluyor.
  const couponDiscount = trdOrder
    ? calculateTrendyolSellerDiscount(trdOrder) - promotionTotal
    : 0;
  if (couponDiscount > 0) {
    items.push({
      ProductId: ZERO_GUID,
      ProductName: withPrefix('trendyol', trdOrder?.coupon?.description || 'Kupon İndirimi'),
      StockCode: null,
      WarehouseId: ZERO_GUID, WarehouseName: null,
      Quantity: 1, Unit: 'adet',
      UnitPrice: -couponDiscount,
      VatRate: 0, SctRate: 0, PriceType: 'VAT_INCLUDED',
      HasRecipe: false, HasMenu: false,
      SelectedOptions: null, TotalPriceDifference: 0,
    });
  }

  // Garanti: Items toplamı = orderTotal
  const itemsTotal = Math.round(items.reduce((sum: number, it: any) => sum + (it.UnitPrice * it.Quantity), 0) * 100) / 100;
  const diff = Math.round((itemsTotal - orderTotal) * 100) / 100;
  if (Math.abs(diff) > 0.01) {
    console.log(`[FATURA][Trendyol] Kalem toplamı (${itemsTotal.toFixed(2)}) ≠ Sipariş toplamı (${orderTotal.toFixed(2)}), fark: ${diff.toFixed(2)}`);
    items.push({
      ProductId: ZERO_GUID,
      ProductName: withPrefix('trendyol', diff > 0 ? 'Platform İndirimi' : 'Fark Düzeltme'),
      StockCode: null,
      WarehouseId: ZERO_GUID, WarehouseName: null,
      Quantity: 1, Unit: 'adet',
      UnitPrice: -diff,
      VatRate: 0, SctRate: 0, PriceType: 'VAT_INCLUDED',
      HasRecipe: false, HasMenu: false,
      SelectedOptions: null, TotalPriceDifference: 0,
    });
  }

  return items;
}

export interface UsePaymentHandlersDeps {
  selectedOrderId: number | null;
  selectedOrder: PackageOrder | null;
  companyId: string | null;
  companyNameCache: string | null;
  screenData: any;
  currentCashier: any;
  currentUserEmail: string | null;
  getirOrders: any[];
  migrosOrders: any[];
  yemeksepetiOrders: any[];
  trendyolOrders: TrendyolOrder[];
  setSnackbar: (data: { open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }) => void;
  setIsPaymentModalOpen: (open: boolean) => void;
  setIsProcessingPayment: (processing: boolean) => void;
  setTrendyolOrders: React.Dispatch<React.SetStateAction<TrendyolOrder[]>>;
  loadPackageOrders: () => Promise<void>;
  fetchGetirOrders: () => Promise<void>;
  fetchMigrosOrders: () => Promise<void>;
  fetchYemeksepetiOrders: () => Promise<void>;
  refreshTrendyolOrders: () => Promise<void>;
  allWarehouses: any[];
  /**
   * ✅ PR2 (Invoice Seller Snapshot Refactor): Backend tarafından çözülen satıcı snapshot.
   * UI / fiş başlığı için kullanılır. Backend zaten ScreenId üzerinden snapshot resolve eder,
   * o yüzden DTO'ya ayrıca bir alan göndermeye gerek yok.
   */
  seller?: SellerSnapshot | null;
  /**
   * ✅ C1 fix (notification screen-availability): Pavo cihaz seçim modal'ı için
   * setter — state PackageOrderScreen tarafında tutulur ki anyOtherModalOpen
   * useMemo'sundan görülebilsin. Aksi halde Pavo seçim ekranı ortasında yemek
   * siparişi geldiğinde NewOrderModal arkadan açılır, ses çalar, ödeme akışı kesilir.
   */
  setPavoP2pSelectOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * ✅ C1 fix (notification screen-availability): REST-P2P online fiş cihaz seçim
   * modal'ı için setter — state PackageOrderScreen tarafında tutulur.
   */
  setRestP2pReceiptSelectOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

// ✅ Sipariş ödemesi işlem anahtarı (IdempotencyKey): ödeme başlarken üretilir, aynı
// sipariş + tutar için başarıya kadar AYNEN tekrar kullanılır. Kasiyer zaman aşımı /
// ağ hatası sonrası tekrar "Öde"ye basarsa (yöntem değişse bile) ve sunucu ilk isteği
// zaten kaydettiyse mükerrer satış yerine ilk sonuç döner. Başarıda silinir.
// Modül düzeyinde tutulur: ekran yeniden bağlansa da anahtar kaybolmaz.
const orderPaymentKeys = new Map<string, string>();
const orderPaymentSlot = (orderId: string | number, amount: number) => `${String(orderId)}|${Math.round((amount || 0) * 100)}`;
const getOrderPaymentKey = (orderId: string | number, amount: number): string => {
  const slot = orderPaymentSlot(orderId, amount);
  let key = orderPaymentKeys.get(slot);
  if (!key) {
    key = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
      ? crypto.randomUUID()
      : `om_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    orderPaymentKeys.set(slot, key);
  }
  return key;
};
const releaseOrderPaymentKey = (orderId: string | number, amount: number) => {
  orderPaymentKeys.delete(orderPaymentSlot(orderId, amount));
};

/**
 * Sipariş ödemesi için sunucuya ödeme niyeti yazar (en fazla 4 sn; hata fırlatmaz).
 * Cihazsız ödemelerde (nakit / harici POS / karma / diğer / online) para zaten alınmıştır:
 * niyet yazılır yazılmaz cihaz sonucu da bildirilir (satış kaydı isteğinden ÖNCE başlar);
 * tarayıcı kaydedemeden kapanırsa sunucu satışı aynı anahtarla tamamlar.
 * Getir siparişleri kapsam dışıdır (dokunulmaz).
 */
const openOrderPaymentIntent = async (args: {
  request: any;
  endpoint: PaymentIntentEndpoint;
  paymentKind: PaymentIntentKind;
  source: string | null | undefined;
  screenData: any;
  cashierName: string | null | undefined;
  /** Cihazsız ödemede true: niyetin hemen ardından cihaz sonucu bildirilir */
  reportNoDeviceResult: boolean;
  fiscalDevice?: string;
  deviceCode?: string | null;
  pavoRequestId?: string | null;
}): Promise<void> => {
  try {
    const key: string | undefined = args.request?.IdempotencyKey;
    if (!key || args.source === 'getir') return;
    const cashier = (args.cashierName || '').trim();
    await createPaymentIntent({
      intentKey: key,
      endpoint: args.endpoint,
      amount: Number(args.request?.TotalAmount ?? ((args.request?.CashAmount || 0) + (args.request?.PosAmount || 0))) || 0,
      paymentKind: args.paymentKind,
      fiscalDevice: args.fiscalDevice || 'yok',
      deviceCode: args.deviceCode ?? null,
      pavoRequestId: args.pavoRequestId ?? null,
      screenId: args.screenData?.id ?? null,
      screenMode: 'paket',
      branchId: args.screenData?.branchId ?? null,
      cashierName: cashier && !cashier.includes('@') ? cashier : null,
      payload: args.request,
    });
    if (args.reportNoDeviceResult) {
      void reportDeviceResult(key, args.request, {
        fiscalResult: 'cihaz-yok',
        endpoint: args.endpoint,
        paymentKind: args.paymentKind,
      });
    }
  } catch (err) {
    console.warn('[ODEME] Ödeme niyeti adımı atlandı:', err);
  }
};

export function usePaymentHandlers(deps: UsePaymentHandlersDeps) {
  const {
    selectedOrderId,
    selectedOrder,
    companyId,
    companyNameCache,
    screenData,
    currentCashier,
    currentUserEmail,
    getirOrders,
    migrosOrders,
    yemeksepetiOrders,
    trendyolOrders,
    setSnackbar,
    setIsPaymentModalOpen,
    setIsProcessingPayment,
    setTrendyolOrders,
    loadPackageOrders,
    fetchGetirOrders,
    fetchMigrosOrders,
    fetchYemeksepetiOrders,
    refreshTrendyolOrders,
    allWarehouses,
    seller, // ✅ PR2: UI/print için seller snapshot (backend resolver authoritatif)
    setPavoP2pSelectOpen,          // ✅ C1 fix: external (PackageOrderScreen)
    setRestP2pReceiptSelectOpen,   // ✅ C1 fix: external (PackageOrderScreen)
  } = deps;
  // seller şu an direkt kullanılmıyor; PrintReceiptService entegrasyonunda
  // (PR2 frontend_print_receipt task'ı) bu hook üzerinden tüketilecek.
  void seller;

  const handleOpenPaymentModal = useCallback(() => {
    if (!selectedOrderId || !selectedOrder) {
      setSnackbar({ open: true, message: 'Lütfen bir sipariş seçin', severity: 'warning' });
      return;
    }

    const isPlatformOrder = ['getir', 'migros', 'yemeksepeti', 'trendyol'].includes(selectedOrder.source);

    console.log('[ODEME_MODAL] Ödeme modal açma isteği:', {
      orderId: selectedOrder.id,
      source: selectedOrder.source,
      status: selectedOrder.status,
      isPaid: selectedOrder.isPaid,
      isOnlinePayment: selectedOrder.isOnlinePayment,
      paymentType: selectedOrder.paymentType,
      isPlatformOrder,
    });

    if (selectedOrder.isPaid) {
      setSnackbar({ open: true, message: 'Bu sipariş için ödeme zaten alınmış.', severity: 'info' });
      return;
    }

    const isPrepaidOrder = selectedOrder.source === 'qr' || selectedOrder.source === 'ecommerce' || selectedOrder.source === 'kiosk';
    if (isPrepaidOrder) {
      const sourceLabel = selectedOrder.source === 'qr' ? 'QR Menü' : selectedOrder.source === 'kiosk' ? 'Kiosk' : 'E-Ticaret';
      setSnackbar({ open: true, message: `${sourceLabel} siparişlerinde ödeme sipariş sırasında peşin alınmıştır.`, severity: 'info' });
      return;
    }

    // Defense-in-depth: RightPanel guard atlanırsa veya programmatic çağrılırsa
    // iptal edilmiş siparişte ödeme modalı açılmasın.
    if (selectedOrder.status === 'cancelled') {
      setSnackbar({ open: true, message: 'Bu sipariş iptal edilmiş, ödeme alınamaz.', severity: 'warning' });
      return;
    }

    const isYemeksepeti = selectedOrder.source === 'yemeksepeti';
    const isPhoneOrder = selectedOrder.source === 'phone';
    const canPay = isPlatformOrder || isPhoneOrder || selectedOrder.status === 'delivered' || (isYemeksepeti && selectedOrder.status === 'onway');
    if (!canPay) {
      setSnackbar({ open: true, message: 'Ödeme almak için sipariş "Teslim Edildi" durumunda olmalı', severity: 'warning' });
      return;
    }

    // Platform siparişleri için paymentSettings zorunlu değil (online ödeme seçeneği her zaman mevcut)
    if (!isPlatformOrder) {
      if (!screenData?.paymentSettings) {
        setSnackbar({ open: true, message: 'Ödeme ayarları yapılandırılmamış. Lütfen ayarlardan ödeme yöntemlerini tanımlayın.', severity: 'error' });
        return;
      }

      const { cashPaymentCashboxId, posOptions, otherOptions } = screenData.paymentSettings;
      if (!cashPaymentCashboxId && posOptions.length === 0 && (!otherOptions || otherOptions.length === 0)) {
        setSnackbar({ open: true, message: 'Hiçbir ödeme seçeneği tanımlanmamış. Lütfen ayarlardan ödeme yöntemlerini tanımlayın.', severity: 'error' });
        return;
      }
    }

    setIsPaymentModalOpen(true);
  }, [selectedOrderId, selectedOrder, screenData?.paymentSettings]);

  /**
   * "Ödeme yap" nakit isteği (fatura yükü). Nakit ödemede ve kurye atanırken kullanılır:
   * kurye uygulamadan ödeme seçince sunucu bu yükü ödeme türüne göre tamamlar.
   */
  const buildCashPaymentRequest = useCallback(async (order: PackageOrder): Promise<any> => {
    if (!companyId || !screenData) throw new Error('Ekran bilgisi eksik');
    const selectedOrder = order;
    const selectedOrderId = order.id;
    const isGetirOrder = order.source === 'getir';
    const isMigrosOrder = order.source === 'migros';
    const isYsOrder = order.source === 'yemeksepeti';
    const isTrendyolOrder = order.source === 'trendyol';
    let paymentRequest: any;

    if (isGetirOrder) {
      const orderTotal = selectedOrder.totalAmount;

      // Cash + POS + Diğer + Online akışlarının ortak helper'ı.
      // Platform indirim/diff kalemlerini kanonik "GETIR - ..." formatında üretir
      // (eski "Getir Platform İndirimi" literal'ı buradan kaldırıldı, withPrefix akışı kullanılıyor).
      const rawInvoiceItems = buildInvoiceItems(selectedOrder, { getirOrders, migrosOrders, yemeksepetiOrders, trendyolOrders });
      const getirResolvedWh = await resolveWarehouseForBranch(companyId, screenData.branchId, screenData.defaultWarehouseId, allWarehouses);
      const invoiceItems = await enrichItemsWithInventory(rawInvoiceItems, companyId, getirResolvedWh, platformResolveContext(selectedOrder, screenData));

      paymentRequest = {

        CompanyName: companyNameCache || null,
        ScreenId: screenData.id,
        AccountId: 1,
        AccountName: `GETİR - ${selectedOrder.customerName || 'Müşteri'}`,
        CashboxId: parseInt(screenData.paymentSettings.cashPaymentCashboxId),
        CashboxName: screenData.paymentSettings.cashPaymentCashboxName || null,
        TotalAmount: orderTotal,
        Currency: 'TRY',
        Description: `Getir Sipariş Ödemesi - ${selectedOrder.shortOrderNo}`,
        CreatedBy: currentCashier?.cashierName || currentUserEmail || 'System',
        BranchId: safeParseBranchId(screenData.branchId),
        BranchName: null,
        IsRestaurantMode: false,
        IsPackageMode: false,
        PackageOrderId: null,
        IsGetirMode: true,
        GetirOrderId: selectedOrderId,
        SkipStockMovement: false,
        CashierId: currentCashier?.cashierId || null,
        CashierName: currentCashier?.cashierName || null,
        Items: invoiceItems,
      };
    } else if (isMigrosOrder) {
      const orderTotal = selectedOrder.totalAmount;

      // Cash + POS + Diğer + Online akışlarının ortak helper'ı.
      // Platform indirim/diff kalemlerini kanonik "MIGROS - ..." formatında üretir
      // (eski "Migros Platform İndirimi" literal'ı buradan kaldırıldı, withPrefix akışı kullanılıyor).
      const rawInvoiceItems = buildInvoiceItems(selectedOrder, { getirOrders, migrosOrders, yemeksepetiOrders, trendyolOrders });
      const migrosResolvedWh = await resolveWarehouseForBranch(companyId, screenData.branchId, screenData.defaultWarehouseId, allWarehouses);
      const migrosInvoiceItems = await enrichItemsWithInventory(rawInvoiceItems, companyId, migrosResolvedWh, platformResolveContext(selectedOrder, screenData));

      paymentRequest = {
  
        CompanyName: companyNameCache || null,
        ScreenId: screenData.id,
        AccountId: 1,
        AccountName: `MIGROS - ${selectedOrder.customerName || 'Musteri'}`,
        CashboxId: parseInt(screenData.paymentSettings.cashPaymentCashboxId),
        CashboxName: screenData.paymentSettings.cashPaymentCashboxName || null,
        TotalAmount: orderTotal,
        Currency: 'TRY',
        Description: `Migros Siparis Odemesi - ${selectedOrder.shortOrderNo}`,
        CreatedBy: currentCashier?.cashierName || currentUserEmail || 'System',
        BranchId: safeParseBranchId(screenData.branchId),
        BranchName: null,
        IsRestaurantMode: false,
        IsPackageMode: false,
        PackageOrderId: null,
        IsGetirMode: false,
        GetirOrderId: null,
        IsMigrosMode: true,
        MigrosOrderId: selectedOrderId,
        SkipStockMovement: false,
        CashierId: currentCashier?.cashierId || null,
        CashierName: currentCashier?.cashierName || null,
        CustomerPhone: selectedOrder.customerPhone || '',
        CustomerAddress: selectedOrder.customerAddress || '',
        Items: migrosInvoiceItems,
      };
    } else if (isYsOrder) {
      const ysOrder = yemeksepetiOrders.find(y => y.id === selectedOrderId);
      const orderTotal = selectedOrder.totalAmount;

      // ✅ Online ile ortak helper: items + toppings + rawJsonData.discounts + diff düzeltme.
      let ysInvoiceItems: any[] = buildYsInvoiceItems(ysOrder, orderTotal, selectedOrder.shortOrderNo);

      const ysResolvedWh = await resolveWarehouseForBranch(companyId, screenData.branchId, screenData.defaultWarehouseId, allWarehouses);
      ysInvoiceItems = await enrichItemsWithInventory(ysInvoiceItems, companyId, ysResolvedWh, platformResolveContext(selectedOrder, screenData));

      paymentRequest = {
  
        CompanyName: companyNameCache || null,
        ScreenId: screenData.id,
        AccountId: 1,
        AccountName: `Y.SEPETİ - ${selectedOrder.customerName || 'Musteri'}`,
        CashboxId: parseInt(screenData.paymentSettings.cashPaymentCashboxId),
        CashboxName: screenData.paymentSettings.cashPaymentCashboxName || null,
        TotalAmount: orderTotal,
        Currency: 'TRY',
        Description: `Yemeksepeti Siparis Odemesi - ${selectedOrder.shortOrderNo}`,
        CreatedBy: currentCashier?.cashierName || currentUserEmail || 'System',
        BranchId: safeParseBranchId(screenData.branchId),
        BranchName: null,
        IsRestaurantMode: false,
        IsPackageMode: false,
        PackageOrderId: null,
        IsGetirMode: false,
        GetirOrderId: null,
        IsMigrosMode: false,
        MigrosOrderId: null,
        IsYemeksepetiMode: true,
        YemeksepetiOrderId: selectedOrderId,
        SkipStockMovement: false,
        CashierId: currentCashier?.cashierId || null,
        CashierName: currentCashier?.cashierName || null,
        CustomerPhone: selectedOrder.customerPhone || '',
        CustomerAddress: selectedOrder.customerAddress || '',
        Items: ysInvoiceItems,
      };
    } else if (isTrendyolOrder) {
      const trdOrder = trendyolOrders.find(o => o.id === selectedOrder.trendyolPackageId);
      const orderTotal = selectedOrder.totalAmount;

      // ✅ Online ile ortak helper: lines + promotions[] + diff düzeltme.
      let trdInvoiceItems: any[] = buildTrendyolInvoiceItems(trdOrder, orderTotal, selectedOrder.shortOrderNo);

      const trdCashResolvedWh = await resolveWarehouseForBranch(companyId, screenData.branchId, screenData.defaultWarehouseId, allWarehouses);
      trdInvoiceItems = await enrichItemsWithInventory(trdInvoiceItems, companyId, trdCashResolvedWh, platformResolveContext(selectedOrder, screenData));

      paymentRequest = {
  
        CompanyName: companyNameCache || null,
        ScreenId: screenData.id,
        AccountId: 1,
        AccountName: `TRENDYOL - ${selectedOrder.customerName || 'Müşteri'}`,
        CashboxId: parseInt(screenData.paymentSettings.cashPaymentCashboxId),
        CashboxName: screenData.paymentSettings.cashPaymentCashboxName || null,
        TotalAmount: orderTotal,
        Currency: 'TRY',
        Description: `Uber Eats Sipariş Ödemesi - ${selectedOrder.shortOrderNo}`,
        CreatedBy: currentCashier?.cashierName || currentUserEmail || 'System',
        BranchId: safeParseBranchId(screenData.branchId),
        BranchName: null,
        IsRestaurantMode: false,
        IsPackageMode: false,
        PackageOrderId: null,
        IsGetirMode: false,
        GetirOrderId: null,
        IsMigrosMode: false,
        MigrosOrderId: null,
        IsYemeksepetiMode: false,
        YemeksepetiOrderId: null,
        IsTrendyolMode: true,
        TrendyolPackageId: selectedOrder.trendyolPackageId || null,
        SkipStockMovement: false,
        CashierId: currentCashier?.cashierId || null,
        CashierName: currentCashier?.cashierName || null,
        CustomerPhone: selectedOrder.customerPhone || '',
        CustomerAddress: selectedOrder.customerAddress || '',
        Items: trdInvoiceItems,
      };
    } else {
      const orderDetails = await getPackageOrderById(selectedOrderId);
      if (!orderDetails.Success || !orderDetails.Data) {
        throw new Error('Siparis detaylari alinamadi');
      }
      
      const orderData = orderDetails.Data;
      
      paymentRequest = {
  
        CompanyName: companyNameCache || null,
        ScreenId: screenData.id,
        AccountId: orderData.AccountId,
        AccountName: orderData.AccountName || selectedOrder.customerName,
        CashboxId: parseInt(screenData.paymentSettings.cashPaymentCashboxId),
        CashboxName: screenData.paymentSettings.cashPaymentCashboxName || null,
        TotalAmount: selectedOrder.totalAmount,
        Currency: 'TRY',
        Description: `Paket Siparis Odemesi - ${selectedOrder.shortOrderNo}`,
        CreatedBy: currentCashier?.cashierName || currentUserEmail || 'System',
        BranchId: safeParseBranchId(screenData.branchId),
        BranchName: null,
        IsRestaurantMode: false,
        IsPackageMode: true,
        PackageOrderId: selectedOrderId,
        IsGetirMode: false,
        GetirOrderId: null,
        SkipStockMovement: false,
        CashierId: currentCashier?.cashierId || null,
        CashierName: currentCashier?.cashierName || null,
        Items: orderData.Items?.map((item: any) => ({
          ProductId: item.ProductId || item.InventoryItemId || '00000000-0000-0000-0000-000000000000',
          ProductName: item.ProductName || item.Name,
          StockCode: item.ProductCode || item.StockCode || null,
          WarehouseId: item.WarehouseId || screenData?.defaultWarehouseId || '00000000-0000-0000-0000-000000000000',
          WarehouseName: item.WarehouseName || null,
          Quantity: item.Quantity || 1,
          Unit: item.UnitName || item.Unit || 'adet',
          UnitPrice: item.UnitPrice || 0,
          VatRate: item.TaxRate || item.VatRate || 0,
          SctRate: item.SctRate || 0,
          PriceType: item.PriceType || 'VAT_INCLUDED',
          HasRecipe: item.HasRecipe || false,
          HasMenu: item.HasMenu || false,
          SelectedOptions: item.SelectedOptions || null,
          TotalPriceDifference: item.TotalPriceDifference || 0,
        })) || [],
      };
    }
    return paymentRequest;
  }, [companyId, companyNameCache, screenData, currentCashier, currentUserEmail, getirOrders, migrosOrders, yemeksepetiOrders, trendyolOrders, allWarehouses]);

  const handleCashPayment = useCallback(async () => {
    if (!selectedOrderId || !selectedOrder || !companyId || !screenData?.paymentSettings?.cashPaymentCashboxId) {
      setSnackbar({ open: true, message: 'Nakit ödeme için gerekli bilgiler eksik', severity: 'error' });
      return;
    }
    
    setIsProcessingPayment(true);
    const isGetirOrder = selectedOrder.source === 'getir';
    const isMigrosOrder = selectedOrder.source === 'migros';
    const isYsOrder = selectedOrder.source === 'yemeksepeti';
    const isTrendyolOrder = selectedOrder.source === 'trendyol';
    
    try {
      console.log('[ODEME] Nakit odeme basliyor...', { orderId: selectedOrderId, amount: selectedOrder.totalAmount, isGetir: isGetirOrder, isMigros: isMigrosOrder, isTrendyol: isTrendyolOrder });
      
      const paymentRequest: any = await buildCashPaymentRequest(selectedOrder);
      paymentRequest.IdempotencyKey = getOrderPaymentKey(selectedOrderId, selectedOrder.totalAmount);

      console.log('💵 [ÖDEME] Nakit ödeme isteği:', paymentRequest);

      await openOrderPaymentIntent({
        request: paymentRequest,
        endpoint: 'process-cash-with-invoice',
        paymentKind: 'nakit',
        source: selectedOrder.source,
        screenData,
        cashierName: currentCashier?.cashierName,
        reportNoDeviceResult: true,
      });

      const { data: result } = await salesApi.post('/api/QuickSaleWithInvoice/process-cash-with-invoice', paymentRequest);

      if (result.Success) {
        console.log('✅ [ÖDEME] Nakit ödeme başarılı:', result);
        releaseOrderPaymentKey(selectedOrderId, selectedOrder.totalAmount);

        setIsPaymentModalOpen(false);
        const sourceLabel = isTrendyolOrder ? 'Uber Eats' : (isYsOrder ? 'Yemeksepeti' : (isMigrosOrder ? 'Migros' : (isGetirOrder ? 'Getir' : 'Paket')));
        setSnackbar({ 
          open: true, 
          message: `${sourceLabel} odeme alindi! Fatura: ${result.InvoiceNumber || result.ReferenceNumber}`, 
          severity: 'success' 
        });

        if (isTrendyolOrder) {
          if (selectedOrder.trendyolPackageId) {
            setTrendyolOrders(prev => prev.map(o =>
              o.id === selectedOrder.trendyolPackageId ? { ...o, gtsPaid: true } : o
            ));
          }
          try {
            if (process.env.REACT_APP_TRENDYOLAPI_URL) {
              await trendyolApi.post('/api/trendyol/order/update-payment-status', {
                PackageId: selectedOrder.trendyolPackageId,
                InvoiceId: result.InvoiceId || null,
                InvoiceNumber: result.InvoiceNumber || null,
                PaidAmount: selectedOrder.totalAmount,
              });
            }
          } catch (e) { console.warn('[ÖDEME] Trendyol payment status güncellenemedi:', e); }
          await refreshTrendyolOrders();
        }
        if (isMigrosOrder) {
          try {
            if (process.env.REACT_APP_MIGROSAPI_URL) {
              await migrosApi.post('/api/migros-orders/update-payment-status', {
                OrderId: selectedOrderId,
                PaymentStatus: 'PAID',
                InvoiceId: result.InvoiceId || null,
                InvoiceNumber: result.InvoiceNumber || null,
                PaidAmount: selectedOrder.totalAmount,
              });
            }
          } catch (e) { console.warn('[ÖDEME] Migros payment status güncellenemedi:', e); }
          await fetchMigrosOrders();
        }
        if (isYsOrder) {
          try {
            if (process.env.REACT_APP_YEMEKSEPETIAPI_URL) {
              await yemeksepetiApi.post('/api/ys-orders/update-payment-status', {
                OrderId: selectedOrderId,
                PaymentStatus: 'PAID',
                InvoiceId: result.InvoiceId || null,
                InvoiceNumber: result.InvoiceNumber || null,
                PaidAmount: selectedOrder.totalAmount,
              });
            }
          } catch (e) { console.warn('[ÖDEME] YS payment status güncellenemedi:', e); }
          await fetchYemeksepetiOrders();
        }
        if (isGetirOrder) {
          await fetchGetirOrders();
        }
        await loadPackageOrders();
      } else {
        throw new Error(result.Message || 'Ödeme işlemi başarısız');
      }
    } catch (error) {
      console.error('❌ [ÖDEME] Nakit ödeme hatası:', error);
      setSnackbar({ 
        open: true, 
        message: `❌ Ödeme hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`, 
        severity: 'error' 
      });
    } finally {
      setIsProcessingPayment(false);
    }
  }, [selectedOrderId, selectedOrder, companyId, companyNameCache, screenData, currentCashier, currentUserEmail, loadPackageOrders, getirOrders, fetchGetirOrders, migrosOrders, fetchMigrosOrders, allWarehouses, buildCashPaymentRequest]);

  /**
   * Karma ödeme (nakit + POS): yalnız telefon / paket siparişinde. Nakit isteği hazırlanır,
   * POS hesabı ve tutarlar eklenip process-partial-with-invoice ile tek faturada alınır.
   */
  const handleMixedPayment = useCallback(async (posOption: {
    id: string;
    label: string;
    bankAccountId: string;
    subAccountId?: string | null;
    bankAccountName?: string | null;
  }, cashAmount: number) => {
    if (!selectedOrderId || !selectedOrder || !companyId || !screenData?.paymentSettings?.cashPaymentCashboxId) {
      setSnackbar({ open: true, message: 'Karma ödeme için nakit kasası ve POS tanımlı olmalı', severity: 'error' });
      return;
    }
    if (['getir', 'migros', 'yemeksepeti', 'trendyol'].includes(selectedOrder.source)) {
      setSnackbar({ open: true, message: 'Karma ödeme yalnız telefon / paket siparişlerinde alınabilir', severity: 'error' });
      return;
    }
    const total = Math.round(selectedOrder.totalAmount * 100) / 100;
    const cash = Math.round(cashAmount * 100) / 100;
    if (!(cash > 0 && cash < total)) {
      setSnackbar({ open: true, message: 'Nakit tutarı sıfırdan büyük, toplamdan küçük olmalı', severity: 'error' });
      return;
    }

    setIsProcessingPayment(true);
    try {
      const base: any = await buildCashPaymentRequest(selectedOrder);
      const { TotalAmount: _total, ...rest } = base;
      const paymentRequest = {
        ...rest,
        IdempotencyKey: getOrderPaymentKey(selectedOrderId, selectedOrder.totalAmount),
        BankAccountId: parseInt(posOption.bankAccountId),
        BankSubAccountId: posOption.subAccountId ? parseInt(posOption.subAccountId) : null,
        BankAccountName: posOption.bankAccountName || posOption.label,
        CashAmount: cash,
        PosAmount: Math.round((total - cash) * 100) / 100,
        Description: `Paket Siparis Odemesi (Karma) - ${selectedOrder.shortOrderNo}`,
        AuthCode: null,
        BatchNo: null,
        MaskedCardNumber: null,
        FiscalTransactionId: null,
        CardType: null,
      };

      await openOrderPaymentIntent({
        request: paymentRequest,
        endpoint: 'process-partial-with-invoice',
        paymentKind: 'karma',
        source: selectedOrder.source,
        screenData,
        cashierName: currentCashier?.cashierName,
        reportNoDeviceResult: true,
      });

      const { data: result } = await salesApi.post('/api/QuickSaleWithInvoice/process-partial-with-invoice', paymentRequest);
      if (result.Success) {
        releaseOrderPaymentKey(selectedOrderId, selectedOrder.totalAmount);
        setIsPaymentModalOpen(false);
        setSnackbar({
          open: true,
          message: `Karma ödeme alındı! Fatura: ${result.InvoiceNumber || result.ReferenceNumber}`,
          severity: 'success',
        });
        await loadPackageOrders();
      } else {
        throw new Error(result.Message || 'Ödeme işlemi başarısız');
      }
    } catch (error: any) {
      const backendMsg = error?.response?.data?.Message || error?.response?.data?.message || error?.response?.data?.ErrorDetails || '';
      setSnackbar({
        open: true,
        message: `Ödeme hatası: ${backendMsg || (error instanceof Error ? error.message : 'Bilinmeyen hata')}`,
        severity: 'error',
      });
    } finally {
      setIsProcessingPayment(false);
    }
  }, [selectedOrderId, selectedOrder, companyId, screenData, currentCashier, loadPackageOrders, buildCashPaymentRequest,
    setIsPaymentModalOpen, setIsProcessingPayment, setSnackbar]);

  const handlePosPayment = useCallback(async (posOption: {
    id: string;
    label: string;
    bankAccountId: string;
    subAccountId: string | null;
    bankAccountName: string | null;
  }) => {
    if (!selectedOrderId || !selectedOrder || !companyId) {
      setSnackbar({ open: true, message: 'POS ödeme için gerekli bilgiler eksik', severity: 'error' });
      return;
    }
    
    setIsProcessingPayment(true);
    const isGetirOrder = selectedOrder.source === 'getir';
    const isMigrosOrder = selectedOrder.source === 'migros';
    const isYsOrder = selectedOrder.source === 'yemeksepeti';
    const isTrendyolOrder = selectedOrder.source === 'trendyol';
    
    try {
      console.log('[ODEME] POS odeme basliyor...', { orderId: selectedOrderId, amount: selectedOrder.totalAmount, pos: posOption.label, isGetir: isGetirOrder, isMigros: isMigrosOrder, isTrendyol: isTrendyolOrder });
      
      let paymentRequest: any;
      
      if (isGetirOrder) {
        const orderTotal = selectedOrder.totalAmount;

        // Cash + POS + Diğer + Online akışlarının ortak helper'ı.
        // Platform indirim/diff kalemlerini kanonik "GETIR - ..." formatında üretir
        // (eski "Getir Platform İndirimi" literal'ı buradan kaldırıldı, withPrefix akışı kullanılıyor).
        const rawInvoiceItems = buildInvoiceItems(selectedOrder, { getirOrders, migrosOrders, yemeksepetiOrders, trendyolOrders });
        const getirPosResolvedWh = await resolveWarehouseForBranch(companyId, screenData?.branchId, screenData?.defaultWarehouseId, allWarehouses);
        const invoiceItems = await enrichItemsWithInventory(rawInvoiceItems, companyId, getirPosResolvedWh, platformResolveContext(selectedOrder, screenData));

        paymentRequest = {

          CompanyName: companyNameCache || null,
          ScreenId: screenData?.id || null,
          AccountId: 1,
          AccountName: `GETİR - ${selectedOrder.customerName || 'Müşteri'}`,
          BankAccountId: parseInt(posOption.bankAccountId),
          BankSubAccountId: posOption.subAccountId ? parseInt(posOption.subAccountId) : null,
          BankAccountName: posOption.bankAccountName || posOption.label,
          TotalAmount: orderTotal,
          Currency: 'TRY',
          Description: `Getir Sipariş Ödemesi (POS) - ${selectedOrder.shortOrderNo}`,
          CreatedBy: currentCashier?.cashierName || currentUserEmail || 'System',
          BranchId: safeParseBranchId(screenData?.branchId),
          BranchName: null,
          IsRestaurantMode: false,
          IsPackageMode: false,
          PackageOrderId: null,
          IsGetirMode: true,
          GetirOrderId: selectedOrderId,
          SkipStockMovement: false,
          CashierId: currentCashier?.cashierId || null,
          CashierName: currentCashier?.cashierName || null,
          AuthCode: null,
          BatchNo: null,
          MaskedCardNumber: null,
          FiscalTransactionId: null,
          CardType: null,
          Items: invoiceItems,
        };
      } else if (isMigrosOrder) {
        const orderTotal = selectedOrder.totalAmount;

        // Cash + POS + Diğer + Online akışlarının ortak helper'ı.
        // Platform indirim/diff kalemlerini kanonik "MIGROS - ..." formatında üretir
        // (eski "Migros Platform İndirimi" literal'ı buradan kaldırıldı, withPrefix akışı kullanılıyor).
        const rawInvoiceItems = buildInvoiceItems(selectedOrder, { getirOrders, migrosOrders, yemeksepetiOrders, trendyolOrders });
        const migrosPosResolvedWh = await resolveWarehouseForBranch(companyId, screenData?.branchId, screenData?.defaultWarehouseId, allWarehouses);
        const migrosInvoiceItems = await enrichItemsWithInventory(rawInvoiceItems, companyId, migrosPosResolvedWh, platformResolveContext(selectedOrder, screenData));

        paymentRequest = {
    
          CompanyName: companyNameCache || null,
          ScreenId: screenData?.id || null,
          AccountId: 1,
          AccountName: `MIGROS - ${selectedOrder.customerName || 'Musteri'}`,
          BankAccountId: parseInt(posOption.bankAccountId),
          BankSubAccountId: posOption.subAccountId ? parseInt(posOption.subAccountId) : null,
          BankAccountName: posOption.bankAccountName || posOption.label,
          TotalAmount: orderTotal,
          Currency: 'TRY',
          Description: `Migros Siparis Odemesi (POS) - ${selectedOrder.shortOrderNo}`,
          CreatedBy: currentCashier?.cashierName || currentUserEmail || 'System',
          BranchId: safeParseBranchId(screenData?.branchId),
          BranchName: null,
          IsRestaurantMode: false,
          IsPackageMode: false,
          PackageOrderId: null,
          IsGetirMode: false,
          GetirOrderId: null,
          IsMigrosMode: true,
          MigrosOrderId: selectedOrderId,
          SkipStockMovement: false,
          CashierId: currentCashier?.cashierId || null,
          CashierName: currentCashier?.cashierName || null,
          CustomerPhone: selectedOrder.customerPhone || '',
          CustomerAddress: selectedOrder.customerAddress || '',
          AuthCode: null,
          BatchNo: null,
          MaskedCardNumber: null,
          FiscalTransactionId: null,
          CardType: null,
          Items: migrosInvoiceItems,
        };
      } else if (isYsOrder) {
        const ysOrder = yemeksepetiOrders.find(y => y.id === selectedOrderId);
        const orderTotal = selectedOrder.totalAmount;

        // ✅ Online ile ortak helper: items + toppings + rawJsonData.discounts + diff düzeltme.
        let ysInvoiceItems: any[] = buildYsInvoiceItems(ysOrder, orderTotal, selectedOrder.shortOrderNo);

        const ysPosResolvedWh = await resolveWarehouseForBranch(companyId, screenData?.branchId, screenData?.defaultWarehouseId, allWarehouses);
        ysInvoiceItems = await enrichItemsWithInventory(ysInvoiceItems, companyId, ysPosResolvedWh, platformResolveContext(selectedOrder, screenData));

        paymentRequest = {
    
          CompanyName: companyNameCache || null,
          ScreenId: screenData?.id || null,
          AccountId: 1,
          AccountName: `Y.SEPETİ - ${selectedOrder.customerName || 'Musteri'}`,
          BankAccountId: parseInt(posOption.bankAccountId),
          BankSubAccountId: posOption.subAccountId ? parseInt(posOption.subAccountId) : null,
          BankAccountName: posOption.bankAccountName || posOption.label,
          TotalAmount: orderTotal,
          Currency: 'TRY',
          Description: `Yemeksepeti Siparis Odemesi (POS) - ${selectedOrder.shortOrderNo}`,
          CreatedBy: currentCashier?.cashierName || currentUserEmail || 'System',
          BranchId: safeParseBranchId(screenData?.branchId),
          BranchName: null,
          IsRestaurantMode: false,
          IsPackageMode: false,
          PackageOrderId: null,
          IsGetirMode: false,
          GetirOrderId: null,
          IsMigrosMode: false,
          MigrosOrderId: null,
          IsYemeksepetiMode: true,
          YemeksepetiOrderId: selectedOrderId,
          SkipStockMovement: false,
          CashierId: currentCashier?.cashierId || null,
          CashierName: currentCashier?.cashierName || null,
          CustomerPhone: selectedOrder.customerPhone || '',
          CustomerAddress: selectedOrder.customerAddress || '',
          AuthCode: null,
          BatchNo: null,
          MaskedCardNumber: null,
          FiscalTransactionId: null,
          CardType: null,
          Items: ysInvoiceItems,
        };
      } else if (isTrendyolOrder) {
        const trdOrder = trendyolOrders.find(o => o.id === selectedOrder.trendyolPackageId);
        const orderTotal = selectedOrder.totalAmount;

        // ✅ Online ile ortak helper: lines + promotions[] + diff düzeltme.
        let trdInvoiceItems: any[] = buildTrendyolInvoiceItems(trdOrder, orderTotal, selectedOrder.shortOrderNo);

        const trdPosResolvedWh = await resolveWarehouseForBranch(companyId, screenData?.branchId, screenData?.defaultWarehouseId, allWarehouses);
        trdInvoiceItems = await enrichItemsWithInventory(trdInvoiceItems, companyId, trdPosResolvedWh, platformResolveContext(selectedOrder, screenData));

        paymentRequest = {
    
          CompanyName: companyNameCache || null,
          ScreenId: screenData?.id || null,
          AccountId: 1,
          AccountName: `TRENDYOL - ${selectedOrder.customerName || 'Müşteri'}`,
          BankAccountId: parseInt(posOption.bankAccountId),
          BankSubAccountId: posOption.subAccountId ? parseInt(posOption.subAccountId) : null,
          BankAccountName: posOption.bankAccountName || posOption.label,
          TotalAmount: orderTotal,
          Currency: 'TRY',
          Description: `Uber Eats Sipariş Ödemesi (POS) - ${selectedOrder.shortOrderNo}`,
          CreatedBy: currentCashier?.cashierName || currentUserEmail || 'System',
          BranchId: safeParseBranchId(screenData?.branchId),
          BranchName: null,
          IsRestaurantMode: false,
          IsPackageMode: false,
          PackageOrderId: null,
          IsGetirMode: false,
          GetirOrderId: null,
          IsMigrosMode: false,
          MigrosOrderId: null,
          IsYemeksepetiMode: false,
          YemeksepetiOrderId: null,
          IsTrendyolMode: true,
          TrendyolPackageId: selectedOrder.trendyolPackageId || null,
          SkipStockMovement: false,
          CashierId: currentCashier?.cashierId || null,
          CashierName: currentCashier?.cashierName || null,
          CustomerPhone: selectedOrder.customerPhone || '',
          CustomerAddress: selectedOrder.customerAddress || '',
          AuthCode: null,
          BatchNo: null,
          MaskedCardNumber: null,
          FiscalTransactionId: null,
          CardType: null,
          Items: trdInvoiceItems,
        };
      } else {
        const orderDetails = await getPackageOrderById(selectedOrderId);
        if (!orderDetails.Success || !orderDetails.Data) {
          throw new Error('Siparis detaylari alinamadi');
        }
        
        const orderData = orderDetails.Data;
        
        paymentRequest = {
    
          CompanyName: companyNameCache || null,
          ScreenId: screenData?.id || null,
          AccountId: orderData.AccountId,
          AccountName: orderData.AccountName || selectedOrder.customerName,
          BankAccountId: parseInt(posOption.bankAccountId),
          BankSubAccountId: posOption.subAccountId ? parseInt(posOption.subAccountId) : null,
          BankAccountName: posOption.bankAccountName || posOption.label,
          TotalAmount: selectedOrder.totalAmount,
          Currency: 'TRY',
          Description: `Paket Siparis Odemesi (POS) - ${selectedOrder.shortOrderNo}`,
          CreatedBy: currentCashier?.cashierName || currentUserEmail || 'System',
          BranchId: safeParseBranchId(screenData?.branchId),
          BranchName: null,
          IsRestaurantMode: false,
          IsPackageMode: true,
          PackageOrderId: selectedOrderId,
          IsGetirMode: false,
          GetirOrderId: null,
          SkipStockMovement: false,
          CashierId: currentCashier?.cashierId || null,
          CashierName: currentCashier?.cashierName || null,
          AuthCode: null,
          BatchNo: null,
          MaskedCardNumber: null,
          FiscalTransactionId: null,
          CardType: null,
          Items: orderData.Items?.map((item: any) => ({
            ProductId: item.ProductId || item.InventoryItemId || '00000000-0000-0000-0000-000000000000',
            ProductName: item.ProductName || item.Name,
            WarehouseId: item.WarehouseId || screenData?.defaultWarehouseId || '00000000-0000-0000-0000-000000000000',
            Quantity: item.Quantity || 1,
            UnitPrice: item.UnitPrice || 0,
            VatRate: item.TaxRate || item.VatRate || 0,
            SctRate: item.SctRate || 0,
            PriceType: item.PriceType || 'VAT_INCLUDED',
            HasRecipe: item.HasRecipe || false,
            HasMenu: item.HasMenu || false,
            SelectedOptions: item.SelectedOptions || null,
            TotalPriceDifference: item.TotalPriceDifference || 0,
          })) || [],
        };
      }
      
      paymentRequest.IdempotencyKey = getOrderPaymentKey(selectedOrderId, selectedOrder.totalAmount);
      console.log('[ODEME] POS odeme istegi:', paymentRequest);

      // Harici POS: yazarkasa/cihaz çağrısı yok — kart bankanın POS'unda çekildi
      await openOrderPaymentIntent({
        request: paymentRequest,
        endpoint: 'process-pos-with-invoice',
        paymentKind: 'pos',
        source: selectedOrder.source,
        screenData,
        cashierName: currentCashier?.cashierName,
        reportNoDeviceResult: true,
      });

      const { data: result } = await salesApi.post('/api/QuickSaleWithInvoice/process-pos-with-invoice', paymentRequest);

      if (result.Success) {
        releaseOrderPaymentKey(selectedOrderId, selectedOrder.totalAmount);
        setIsPaymentModalOpen(false);
        const sourceLabel = isTrendyolOrder ? 'Uber Eats' : (isYsOrder ? 'Yemeksepeti' : (isMigrosOrder ? 'Migros' : (isGetirOrder ? 'Getir' : 'Paket')));
        setSnackbar({ 
          open: true, 
          message: `${sourceLabel} odeme alindi! Fatura: ${result.InvoiceNumber || result.ReferenceNumber}`, 
          severity: 'success' 
        });

        if (isTrendyolOrder) {
          if (selectedOrder.trendyolPackageId) {
            setTrendyolOrders(prev => prev.map(o =>
              o.id === selectedOrder.trendyolPackageId ? { ...o, gtsPaid: true } : o
            ));
          }
          try {
            if (process.env.REACT_APP_TRENDYOLAPI_URL) {
              await trendyolApi.post('/api/trendyol/order/update-payment-status', {
                PackageId: selectedOrder.trendyolPackageId,
                InvoiceId: result.InvoiceId || null,
                InvoiceNumber: result.InvoiceNumber || null,
                PaidAmount: selectedOrder.totalAmount,
              });
            }
          } catch (e) { console.warn('[ÖDEME] Trendyol payment status güncellenemedi:', e); }
          await refreshTrendyolOrders();
        }
        if (isMigrosOrder) {
          try {
            if (process.env.REACT_APP_MIGROSAPI_URL) {
              await migrosApi.post('/api/migros-orders/update-payment-status', {
                OrderId: selectedOrderId,
                PaymentStatus: 'PAID',
                InvoiceId: result.InvoiceId || null,
                InvoiceNumber: result.InvoiceNumber || null,
                PaidAmount: selectedOrder.totalAmount,
              });
            }
          } catch (e) { console.warn('[ÖDEME] Migros payment status güncellenemedi:', e); }
          await fetchMigrosOrders();
        }
        if (isYsOrder) {
          try {
            if (process.env.REACT_APP_YEMEKSEPETIAPI_URL) {
              await yemeksepetiApi.post('/api/ys-orders/update-payment-status', {
                OrderId: selectedOrderId,
                PaymentStatus: 'PAID',
                InvoiceId: result.InvoiceId || null,
                InvoiceNumber: result.InvoiceNumber || null,
                PaidAmount: selectedOrder.totalAmount,
              });
            }
          } catch (e) { console.warn('[ÖDEME] YS payment status güncellenemedi:', e); }
          await fetchYemeksepetiOrders();
        }
        if (isGetirOrder) { await fetchGetirOrders(); }
        await loadPackageOrders();
      } else {
        throw new Error(result.Message || 'Odeme islemi basarisiz');
      }
    } catch (error: any) {
      console.error('[ODEME] POS odeme hatasi:', error);
      const backendMsg = error?.response?.data?.Message || error?.response?.data?.message || error?.response?.data?.ErrorDetails || '';
      const displayMsg = backendMsg || (error instanceof Error ? error.message : 'Bilinmeyen hata');
      console.error('[ODEME] Backend hata detayi:', error?.response?.data);
      setSnackbar({ 
        open: true, 
        message: `Ödeme hatası: ${displayMsg}`, 
        severity: 'error' 
      });
    } finally {
      setIsProcessingPayment(false);
    }
  }, [selectedOrderId, selectedOrder, companyId, companyNameCache, screenData, currentCashier, currentUserEmail, loadPackageOrders, getirOrders, fetchGetirOrders, migrosOrders, fetchMigrosOrders, allWarehouses]);

  const handleOtherPayment = useCallback(async (otherOption: {
    id: string;
    label: string;
    cashboxId: string;
    cashboxName: string | null;
  }) => {
    if (!selectedOrderId || !selectedOrder || !companyId) {
      setSnackbar({ open: true, message: 'Diğer ödeme için gerekli bilgiler eksik', severity: 'error' });
      return;
    }

    setIsProcessingPayment(true);
    const isGetirOrder = selectedOrder.source === 'getir';
    const isMigrosOrder = selectedOrder.source === 'migros';
    const isYsOrder = selectedOrder.source === 'yemeksepeti';
    const isTrendyolOrder = selectedOrder.source === 'trendyol';

    try {
      console.log('[ODEME] Diger odeme basliyor...', { orderId: selectedOrderId, amount: selectedOrder.totalAmount, method: otherOption.label });

      const rawInvoiceItems = buildInvoiceItems(selectedOrder, { getirOrders, migrosOrders, yemeksepetiOrders, trendyolOrders });
      const isPlatformOrderEarly = isGetirOrder || isMigrosOrder || isYsOrder || isTrendyolOrder;
      let invoiceItems = rawInvoiceItems;
      if (isPlatformOrderEarly) {
        const otherResolvedWh = await resolveWarehouseForBranch(companyId, screenData?.branchId, screenData?.defaultWarehouseId, allWarehouses);
        invoiceItems = await enrichItemsWithInventory(rawInvoiceItems, companyId, otherResolvedWh, platformResolveContext(selectedOrder, screenData));
      }

      const sourcePrefix = isGetirOrder ? 'GETİR' : isMigrosOrder ? 'MIGROS' : isYsOrder ? 'Y.SEPETİ' : isTrendyolOrder ? 'TRENDYOL' : 'PAKET';

      const isPlatformOrder = isGetirOrder || isMigrosOrder || isYsOrder || isTrendyolOrder;
      const onlinePlatform = isGetirOrder ? 'GETİR' : isMigrosOrder ? 'MİGROS' : isYsOrder ? 'YEMEKSEPETİ' : isTrendyolOrder ? 'TRENDYOL' : undefined;

      const paymentRequest: any = {
  
        CompanyName: companyNameCache || null,
        ScreenId: screenData?.id || null,
        AccountId: isPlatformOrder ? 0 : undefined,
        AccountName: `${sourcePrefix} - ${selectedOrder.customerName || 'Musteri'}`,
        CashboxId: parseInt(otherOption.cashboxId),
        CashboxName: otherOption.cashboxName || otherOption.label,
        TotalAmount: selectedOrder.totalAmount,
        Currency: 'TRY',
        Description: `${sourcePrefix} Siparis Odemesi (${otherOption.label}) - ${selectedOrder.shortOrderNo}`,
        CreatedBy: currentCashier?.cashierName || currentUserEmail || 'System',
        BranchId: safeParseBranchId(screenData?.branchId),
        BranchName: null,
        IsRestaurantMode: false,
        CashierId: currentCashier?.cashierId || null,
        CashierName: currentCashier?.cashierName || null,
        OtherPaymentOptionId: otherOption.id,
        OtherPaymentLabel: otherOption.label,
        PaymentProviderType: otherOption.label.toUpperCase(),
        PaymentProviderName: otherOption.label,
        SkipStockMovement: false,
        CustomerPhone: isPlatformOrder ? (selectedOrder.customerPhone || null) : null,
        CustomerAddress: isPlatformOrder ? (selectedOrder.customerAddress || null) : null,
        OnlinePlatform: onlinePlatform,
        IsPackageMode: !isPlatformOrder,
        PackageOrderId: !isPlatformOrder ? selectedOrderId : null,
        Items: invoiceItems,
      };

      if (!isPlatformOrder && !paymentRequest.AccountId) {
        const orderDetails = await getPackageOrderById(selectedOrderId);
        if (!orderDetails.Success || !orderDetails.Data) {
          // Eskiden sessizce devam ediliyordu → AccountId gönderilmiyor (0) ve sunucu
          // "Cari hesap bulunamadı (ID: 0)" dönüyordu. Gerçek nedeni göster.
          throw new Error('Sipariş detayları alınamadı (cari hesap belirlenemedi). Lütfen tekrar deneyin.');
        }
        if (orderDetails.Success && orderDetails.Data) {
          paymentRequest.AccountId = orderDetails.Data.AccountId;
          paymentRequest.AccountName = orderDetails.Data.AccountName || selectedOrder.customerName;
          paymentRequest.Items = orderDetails.Data.Items?.map((item: any) => ({
            ProductId: item.ProductId || item.InventoryItemId || '00000000-0000-0000-0000-000000000000',
            ProductName: item.ProductName || item.Name,
            StockCode: null,
            WarehouseId: item.WarehouseId || screenData?.defaultWarehouseId || '00000000-0000-0000-0000-000000000000',
            WarehouseName: item.WarehouseName || null,
            Quantity: item.Quantity || 1,
            Unit: item.UnitName || item.Unit || 'adet',
            UnitPrice: item.UnitPrice || 0,
            VatRate: item.TaxRate || item.VatRate || 0,
            SctRate: item.SctRate || 0,
            PriceType: item.PriceType || 'VAT_INCLUDED',
            HasRecipe: item.HasRecipe || false,
            HasMenu: item.HasMenu || false,
          })) || [];
          paymentRequest.SkipStockMovement = false;
        }
      }

      paymentRequest.IdempotencyKey = getOrderPaymentKey(selectedOrderId, selectedOrder.totalAmount);
      await openOrderPaymentIntent({
        request: paymentRequest,
        endpoint: 'process-other-payment-with-invoice',
        paymentKind: 'diger',
        source: selectedOrder.source,
        screenData,
        cashierName: currentCashier?.cashierName,
        reportNoDeviceResult: true,
      });
      const { data: result } = await salesApi.post('/api/QuickSaleWithInvoice/process-other-payment-with-invoice', paymentRequest);

      if (result.Success) {
        releaseOrderPaymentKey(selectedOrderId, selectedOrder.totalAmount);
        setIsPaymentModalOpen(false);
        const sourceLabel = isTrendyolOrder ? 'Uber Eats' : (isYsOrder ? 'Yemeksepeti' : (isMigrosOrder ? 'Migros' : (isGetirOrder ? 'Getir' : 'Paket')));
        setSnackbar({
          open: true,
          message: `${sourceLabel} ${otherOption.label} odeme alindi! Fatura: ${result.InvoiceNumber || result.ReferenceNumber}`,
          severity: 'success'
        });

        await postPaymentRefresh(selectedOrder, result, { isTrendyolOrder, isMigrosOrder, isYsOrder, isGetirOrder });
      } else {
        throw new Error(result.Message || 'Odeme islemi basarisiz');
      }
    } catch (error) {
      console.error('[ODEME] Diger odeme hatasi:', error);
      setSnackbar({
        open: true,
        message: `Odeme hatasi: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`,
        severity: 'error'
      });
    } finally {
      setIsProcessingPayment(false);
    }
  }, [selectedOrderId, selectedOrder, companyId, companyNameCache, screenData, currentCashier, currentUserEmail, loadPackageOrders, getirOrders, fetchGetirOrders, migrosOrders, fetchMigrosOrders, allWarehouses]);

  const handleOnlinePaymentRecord = useCallback(async (order: PackageOrder, isManual: boolean = false) => {
    const logPrefix = isManual ? '🖐️ [MANUEL_ONLINE_ODEME]' : '🤖 [OTO_ONLINE_ODEME]';

    console.log(`${logPrefix} ====== ONLINE ODEME KAYDI BASLADI ======`);
    console.log(`${logPrefix} Sipariş:`, {
      orderId: order.id,
      source: order.source,
      shortOrderNo: order.shortOrderNo,
      customerName: order.customerName,
      totalAmount: order.totalAmount,
      isPaid: order.isPaid,
      isOnlinePayment: order.isOnlinePayment,
      status: order.status,
      trendyolPackageId: order.trendyolPackageId,
    });

    if (!companyId || !screenData) {
      console.error(`${logPrefix} HATA: CompanyId veya screenData eksik!`, { companyId, hasScreenData: !!screenData });
      if (isManual) setSnackbar({ open: true, message: 'Şirket bilgisi veya ekran ayarları eksik!', severity: 'error' });
      return;
    }

    if (order.isPaid) {
      console.log(`${logPrefix} Sipariş zaten ödenmiş, atlanıyor:`, order.id);
      if (isManual) setSnackbar({ open: true, message: 'Bu sipariş için ödeme zaten kayıt edilmiş.', severity: 'info' });
      return;
    }

    try {
      console.log(`${logPrefix} Fatura kalemleri oluşturuluyor...`);

      // OnlinePlatform ve AccountName protokol kimliğidir: backend cari/fiş
      // prefix eşlemesi (`TRENDYOL` → TRD-) ve kayıtlı ödeme ayarı label'ı
      // buna bakar. Ekranda Uber Eats yazması bu değeri değiştirmez.
      let platformName = '';
      if (order.source === 'getir') platformName = 'GETİR YEMEK';
      else if (order.source === 'trendyol') platformName = 'TRENDYOL YEMEK';
      else if (order.source === 'migros') platformName = 'MİGROS YEMEK';
      else if (order.source === 'yemeksepeti') platformName = 'YEMEK SEPETİ';
      else platformName = order.source?.toUpperCase() || 'ONLINE';

      const rawOnlineItems = buildInvoiceItems(order, { getirOrders, migrosOrders, yemeksepetiOrders, trendyolOrders });
      console.log(`${logPrefix} Fatura kalemleri (raw):`, rawOnlineItems.length, 'kalem', rawOnlineItems.map((i: any) => `${i.ProductName}: ${i.UnitPrice}x${i.Quantity}`));

      const onlineResolvedWh = await resolveWarehouseForBranch(companyId, screenData.branchId, screenData.defaultWarehouseId, allWarehouses);
      console.log(`${logPrefix} Depo çözümlendi:`, onlineResolvedWh);

      const invoiceItems = await enrichItemsWithInventory(rawOnlineItems, companyId, onlineResolvedWh, platformResolveContext(order, screenData));
      console.log(`${logPrefix} Fatura kalemleri (enriched):`, invoiceItems.length, 'kalem');

      // Anahtar sipariş başına SABİT olmalı. Eskiden dakika damgası eklenirdi; bu
      // yüzden dakika sınırını aşan bir tekrar deneme yeni anahtar üretiyor ve
      // mükerrer kayıt korumasını devre dışı bırakıyordu. Bir platform siparişi
      // yalnızca bir kez satışa dönüşür, dolayısıyla sipariş kimliği yeterlidir.
      const idempotencyKey = `online_${order.source}_${order.id}`;
      console.log(`${logPrefix} IdempotencyKey:`, idempotencyKey);

      const isGetir = order.source === 'getir';
      const isMigros = order.source === 'migros';
      const isYs = order.source === 'yemeksepeti';
      const isTrendyol = order.source === 'trendyol';

      const paymentRequest: any = {
        IdempotencyKey: idempotencyKey,
  
        CompanyName: companyNameCache || null,
        ScreenId: screenData.id,
        AccountId: 1,
        AccountName: `${platformName} ONLİNE - ${order.customerName || 'Musteri'}`,
        OnlinePlatform: platformName,
        PaymentMethod: 'online',
        CashboxId: null,
        CashboxName: null,
        BankAccountId: null,
        BankAccountName: null,
        TotalAmount: order.totalAmount,
        Currency: 'TRY',
        Description: `${platformName} Online Odeme - ${order.shortOrderNo}`,
        CreatedBy: currentCashier?.cashierName || currentUserEmail || 'System',
        BranchId: safeParseBranchId(screenData.branchId),
        BranchName: null,
        IsRestaurantMode: false,
        CustomerPhone: order.customerPhone || '',
        CustomerAddress: order.customerAddress || '',
        CashierId: currentCashier?.cashierId || null,
        CashierName: currentCashier?.cashierName || null,
        SkipStockMovement: false,
        IsPackageMode: false,
        PackageOrderId: null,
        IsGetirMode: isGetir,
        GetirOrderId: isGetir ? order.id : null,
        IsMigrosMode: isMigros,
        MigrosOrderId: isMigros ? order.id : null,
        IsYemeksepetiMode: isYs,
        YemeksepetiOrderId: isYs ? order.id : null,
        IsTrendyolMode: isTrendyol,
        TrendyolPackageId: isTrendyol ? order.trendyolPackageId : null,
        Items: invoiceItems,
      };

      console.log(`${logPrefix} API isteği gönderiliyor...`, {
        url: '/api/QuickSaleWithInvoice/process-online-payment-with-invoice',
        CompanyId: paymentRequest.CompanyId,
        TotalAmount: paymentRequest.TotalAmount,
        OnlinePlatform: paymentRequest.OnlinePlatform,
        IsGetirMode: paymentRequest.IsGetirMode,
        IsMigrosMode: paymentRequest.IsMigrosMode,
        IsYemeksepetiMode: paymentRequest.IsYemeksepetiMode,
        IsTrendyolMode: paymentRequest.IsTrendyolMode,
        ItemCount: paymentRequest.Items?.length,
      });

      await openOrderPaymentIntent({
        request: paymentRequest,
        endpoint: 'process-online-payment-with-invoice',
        paymentKind: 'online',
        source: order.source,
        screenData,
        cashierName: currentCashier?.cashierName,
        reportNoDeviceResult: true,
      });

      let result: any;
      try {
        const { data } = await salesApi.post('/api/QuickSaleWithInvoice/process-online-payment-with-invoice', paymentRequest);
        result = data;
      } catch (err: any) {
        console.error(`${logPrefix} API HATA:`, err.response?.status, err.response?.data?.toString?.()?.substring(0, 500));
        if (isManual) setSnackbar({ open: true, message: `Ödeme kaydı başarısız: HTTP ${err.response?.status || 'N/A'}`, severity: 'error' });
        return;
      }
      console.log(`${logPrefix} API sonuç:`, {
        Success: result.Success,
        InvoiceId: result.InvoiceId,
        InvoiceNumber: result.InvoiceNumber,
        ReferenceNumber: result.ReferenceNumber,
        ReceiptId: result.ReceiptId,
        Message: result.Message,
      });

      if (result.Success) {
        console.log(`${logPrefix} ✅ KAYIT BASARILI! Fatura No: ${result.InvoiceNumber}, Fiş ID: ${result.ReceiptId}`);

        // ✅ Fatura kaydı başarılı: modalı HEMEN kapat (nakit/POS akışıyla aynı davranış).
        // Platform durum güncellemesi ve liste yenilemeleri arka planda devam eder;
        // yavaş internette kasiyer dakikalarca açık modal beklemesin.
        if (isManual) {
          setSnackbar({ open: true, message: `✅ Online ödeme kaydı başarılı! Fatura: ${result.InvoiceNumber || '-'}`, severity: 'success' });
          setIsPaymentModalOpen(false);
        }

        const updatePaymentStatusWithRetry = async (api: { post: (url: string, data: any) => Promise<any> }, endpoint: string, body: any, label: string) => {
          console.log(`${logPrefix} ${label} platform ödeme durumu güncelleniyor...`, { endpoint, body });
          for (let attempt = 1; attempt <= 2; attempt++) {
            try {
              const { data: respData } = await api.post(endpoint, body);
              console.log(`${logPrefix} ✅ ${label} payment status güncellendi (deneme ${attempt}):`, respData);
              return;
            } catch (e: any) {
              console.warn(`${logPrefix} ⚠️ ${label} payment status hata (deneme ${attempt}):`, e?.response?.data || e);
            }
            if (attempt < 2) await new Promise(r => setTimeout(r, 1000));
          }
        };

        if (isTrendyol && order.trendyolPackageId) {
          setTrendyolOrders(prev => prev.map(o =>
            o.id === order.trendyolPackageId ? { ...o, gtsPaid: true } : o
          ));
          if (process.env.REACT_APP_TRENDYOLAPI_URL) {
            await updatePaymentStatusWithRetry(
              trendyolApi,
              '/api/trendyol/order/update-payment-status',
              { PackageId: order.trendyolPackageId, InvoiceId: result.InvoiceId || null, InvoiceNumber: result.InvoiceNumber || null, PaidAmount: order.totalAmount },
              'Trendyol'
            );
          }
        }

        if (isMigros) {
          if (process.env.REACT_APP_MIGROSAPI_URL) {
            await updatePaymentStatusWithRetry(
              migrosApi,
              '/api/migros-orders/update-payment-status',
              { OrderId: order.id, PaymentStatus: 'PAID', InvoiceId: result.InvoiceId || null, InvoiceNumber: result.InvoiceNumber || null, PaidAmount: order.totalAmount },
              'Migros'
            );
          }
        }

        if (isYs) {
          if (process.env.REACT_APP_YEMEKSEPETIAPI_URL) {
            await updatePaymentStatusWithRetry(
              yemeksepetiApi,
              '/api/ys-orders/update-payment-status',
              { OrderId: order.id, PaymentStatus: 'PAID', InvoiceId: result.InvoiceId || null, InvoiceNumber: result.InvoiceNumber || null, PaidAmount: order.totalAmount },
              'Yemeksepeti'
            );
          }
        }

        if (isGetir) {
          if (process.env.REACT_APP_GETIRAPI_URL) {
            await updatePaymentStatusWithRetry(
              getirApi,
              '/api/getir-orders/update-payment-status',
              { OrderId: order.id, PaymentStatus: 'PAID', InvoiceId: result.InvoiceId || null, InvoiceNumber: result.InvoiceNumber || null, PaidAmount: order.totalAmount },
              'Getir'
            );
          }
        }

        console.log(`${logPrefix} Sipariş listeleri yenileniyor...`);
        try {
          if (isTrendyol) await refreshTrendyolOrders();
          else if (isMigros) await fetchMigrosOrders();
          else if (isYs) await fetchYemeksepetiOrders();
          else if (isGetir) await fetchGetirOrders();
          await loadPackageOrders();
          console.log(`${logPrefix} ✅ Sipariş listeleri yenilendi`);
        } catch (e) {
          console.warn(`${logPrefix} Sipariş listesi yenilenemedi:`, e);
        }

        console.log(`${logPrefix} ====== ONLINE ODEME KAYDI TAMAMLANDI ======`);
      } else {
        console.error(`${logPrefix} ❌ API başarısız döndü:`, result.Message);
        if (isManual) setSnackbar({ open: true, message: `Ödeme kaydı başarısız: ${result.Message || 'Bilinmeyen hata'}`, severity: 'error' });
      }
    } catch (error) {
      console.error(`${logPrefix} ❌ HATA:`, error);
      if (isManual) setSnackbar({ open: true, message: `Ödeme hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`, severity: 'error' });
    }
  }, [companyId, companyNameCache, screenData, currentCashier, currentUserEmail, getirOrders, migrosOrders, yemeksepetiOrders, trendyolOrders, setTrendyolOrders, allWarehouses, loadPackageOrders, fetchGetirOrders, fetchMigrosOrders, fetchYemeksepetiOrders, refreshTrendyolOrders, setSnackbar, setIsPaymentModalOpen]);

  const handleOnlinePaymentFromModal = useCallback(async () => {
    if (!selectedOrder) {
      setSnackbar({ open: true, message: 'Sipariş seçili değil!', severity: 'error' });
      return;
    }
    console.log('🖐️ [MANUEL_ONLINE_ODEME] Kasiyer modaldan online ödeme tetikledi:', {
      orderId: selectedOrder.id,
      source: selectedOrder.source,
      amount: selectedOrder.totalAmount,
    });
    setIsProcessingPayment(true);
    try {
      await handleOnlinePaymentRecord(selectedOrder, true);
    } finally {
      setIsProcessingPayment(false);
    }
  }, [selectedOrder, handleOnlinePaymentRecord, setSnackbar, setIsProcessingPayment]);

  function buildInvoiceItems(
    order: PackageOrder,
    sources: { getirOrders: any[]; migrosOrders: any[]; yemeksepetiOrders: any[]; trendyolOrders: TrendyolOrder[] }
  ): any[] {
    const orderTotal = order.totalAmount;
    const fallbackItem = (label: string) => [{ ProductId: ZERO_GUID, ProductName: `${label} #${order.shortOrderNo}`, StockCode: null, WarehouseId: ZERO_GUID, WarehouseName: null, Quantity: 1, Unit: 'adet', UnitPrice: orderTotal, VatRate: 0, SctRate: 0, PriceType: 'VAT_INCLUDED', HasRecipe: false, HasMenu: false }];

    let items: any[] = [];

    if (order.source === 'getir') {
      const getirOrder = sources.getirOrders.find(g => g.id === order.id);
      const rawItems = getirOrder?.items || [];
      if (rawItems.length > 0) {
        items = rawItems.map((item: any) => ({
          ProductId: ZERO_GUID, ProductName: item.name || 'Getir Urun', StockCode: null,
          WarehouseId: ZERO_GUID, WarehouseName: null,
          Quantity: item.quantity || 1, Unit: 'adet',
          UnitPrice: item.price || 0,
          VatRate: 0, SctRate: 0, PriceType: 'VAT_INCLUDED', HasRecipe: false, HasMenu: false,
        }));
        const gTotalPrice = getirOrder?.totalPrice || 0;
        const gDiscountedPrice = getirOrder?.totalDiscountedPrice;
        if (gDiscountedPrice != null && gDiscountedPrice > 0 && gTotalPrice > gDiscountedPrice) {
          items.push({
            ProductId: ZERO_GUID,
            ProductName: withPrefix('getir', 'Platform İndirimi'),
            StockCode: null,
            WarehouseId: ZERO_GUID, WarehouseName: null,
            Quantity: 1, Unit: 'adet',
            UnitPrice: -(gTotalPrice - gDiscountedPrice),
            VatRate: 0, SctRate: 0, PriceType: 'VAT_INCLUDED',
            HasRecipe: false, HasMenu: false,
          });
        }
      } else {
        return fallbackItem('Getir Siparis');
      }
    } else if (order.source === 'migros') {
      const migrosOrder = sources.migrosOrders.find(m => m.id === order.id);
      const rawItems = migrosOrder?.items || [];
      if (rawItems.length > 0) {
        items = rawItems.map((item: any) => ({
          __platformLine: {
            id: (item.productId ?? item.ProductId) ? String(item.productId ?? item.ProductId) : null,
            name: item.name || 'Migros Urun',
          } as PlatformLineTag,
          ProductId: ZERO_GUID, ProductName: item.name || 'Migros Urun', StockCode: null,
          WarehouseId: ZERO_GUID, WarehouseName: null,
          Quantity: item.amount || 1, Unit: 'adet',
          UnitPrice: (item.unitPrice || item.price || 0) / 100,
          VatRate: 0, SctRate: 0, PriceType: 'VAT_INCLUDED', HasRecipe: false, HasMenu: false,
        }));
      } else {
        return fallbackItem('Migros Siparis');
      }
    } else if (order.source === 'yemeksepeti') {
      // Cash + POS + Online akışlarının ortak helper'ı (module-level).
      // İndirim kalemlerini ve diff düzeltmeyi kendisi ekler.
      const ysOrder = sources.yemeksepetiOrders.find(y => y.id === order.id);
      return buildYsInvoiceItems(ysOrder, orderTotal, order.shortOrderNo);
    } else if (order.source === 'trendyol') {
      // Cash + POS + Online akışlarının ortak helper'ı (module-level).
      // Promosyon kalemlerini ve diff düzeltmeyi kendisi ekler.
      const trdOrder = sources.trendyolOrders.find(o => o.id === order.trendyolPackageId);
      return buildTrendyolInvoiceItems(trdOrder, orderTotal, order.shortOrderNo);
    } else {
      return fallbackItem('Siparis');
    }

    const itemsTotal = Math.round(items.reduce((sum: number, it: any) => sum + (it.UnitPrice * it.Quantity), 0) * 100) / 100;
    const diff = Math.round((itemsTotal - orderTotal) * 100) / 100;

    if (Math.abs(diff) > 0.01) {
      console.log(`[FATURA] Kalem toplamı (${itemsTotal.toFixed(2)}) ≠ Sipariş toplamı (${orderTotal.toFixed(2)}), fark: ${diff.toFixed(2)}`);
      items.push({
        ProductId: ZERO_GUID,
        ProductName: withPrefix(order.source || '', diff > 0 ? 'Platform İndirimi' : 'Fark Düzeltme'),
        StockCode: null,
        WarehouseId: ZERO_GUID,
        WarehouseName: null,
        Quantity: 1,
        Unit: 'adet',
        UnitPrice: -diff,
        VatRate: 0, SctRate: 0, PriceType: 'VAT_INCLUDED',
        HasRecipe: false, HasMenu: false,
      });
    }

    return items;
  }

  async function postPaymentRefresh(
    order: PackageOrder,
    result: any,
    flags: { isTrendyolOrder: boolean; isMigrosOrder: boolean; isYsOrder: boolean; isGetirOrder: boolean }
  ) {
    if (flags.isTrendyolOrder) {
      if (order.trendyolPackageId) {
        setTrendyolOrders(prev => prev.map(o =>
          o.id === order.trendyolPackageId ? { ...o, gtsPaid: true } : o
        ));
      }
      try {
        if (process.env.REACT_APP_TRENDYOLAPI_URL) {
          await trendyolApi.post('/api/trendyol/order/update-payment-status', {
            PackageId: order.trendyolPackageId,
            InvoiceId: result.InvoiceId || null,
            InvoiceNumber: result.InvoiceNumber || null,
            PaidAmount: order.totalAmount,
          });
        }
      } catch (e) { console.warn('[ODEME] Trendyol payment status guncellenemedi:', e); }
      await refreshTrendyolOrders();
    }
    if (flags.isMigrosOrder) {
      try {
        if (process.env.REACT_APP_MIGROSAPI_URL) {
          await migrosApi.post('/api/migros-orders/update-payment-status', {
            OrderId: order.id,
            PaymentStatus: 'PAID',
            InvoiceId: result.InvoiceId || null,
            InvoiceNumber: result.InvoiceNumber || null,
            PaidAmount: order.totalAmount,
          });
        }
      } catch (e) { console.warn('[ODEME] Migros payment status guncellenemedi:', e); }
      await fetchMigrosOrders();
    }
    if (flags.isYsOrder) {
      try {
        if (process.env.REACT_APP_YEMEKSEPETIAPI_URL) {
          await yemeksepetiApi.post('/api/ys-orders/update-payment-status', {
            OrderId: order.id,
            PaymentStatus: 'PAID',
            InvoiceId: result.InvoiceId || null,
            InvoiceNumber: result.InvoiceNumber || null,
            PaidAmount: order.totalAmount,
          });
        }
      } catch (e) { console.warn('[ODEME] YS payment status guncellenemedi:', e); }
      await fetchYemeksepetiOrders();
    }
    if (flags.isGetirOrder) {
      try {
        if (process.env.REACT_APP_GETIRAPI_URL) {
          await getirApi.post('/api/getir-orders/update-payment-status', {
            OrderId: order.id,
            PaymentStatus: 'PAID',
            InvoiceId: result.InvoiceId || null,
            InvoiceNumber: result.InvoiceNumber || null,
            PaidAmount: order.totalAmount,
          });
          console.log('[ODEME] Getir payment status SQL guncellendi:', order.id);
        }
      } catch (e) { console.warn('[ODEME] Getir payment status guncellenemedi:', e); }
      await fetchGetirOrders();
    }
    await loadPackageOrders();
  }

  // ✅ C1 fix: pavoP2pSelectOpen state PackageOrderScreen tarafında tutuluyor
  // (anyOtherModalOpen useMemo'sundan görünebilsin diye). Setter dışarıdan geliyor.

  const openPavoP2pSelect = useCallback(() => {
    setPavoP2pSelectOpen(true);
  }, [setPavoP2pSelectOpen]);

  const handlePavoP2pPayment = useCallback(async (pavoDeviceCode: string) => {
    if (!pavoDeviceCode) {
      setSnackbar({ open: true, message: 'Lütfen bir PavoPay cihazı seçin', severity: 'error' });
      return;
    }
    if (!selectedOrder || !selectedOrderId || !companyId) {
      setSnackbar({ open: true, message: 'Sipariş veya şirket bilgisi eksik', severity: 'error' });
      return;
    }

    // PavoPay icin bir POS banka hesabi sec (ayarlarda "PAVO" iceren varsa onu, yoksa ilk POS option'i)
    const posOptions = (screenData?.paymentSettings?.posOptions || []) as Array<{
      id: string; label: string; bankAccountId: string; subAccountId: string | null; bankAccountName: string | null;
    }>;
    if (posOptions.length === 0) {
      setSnackbar({ open: true, message: 'Bu ekranda tanimli bir POS banka hesabi yok. PavoPay icin en az bir POS banka hesabi tanimli olmali.', severity: 'error' });
      return;
    }
    const pavoPos = posOptions.find(p =>
      (p.label || '').toUpperCase().includes('PAVO') ||
      (p.bankAccountName || '').toUpperCase().includes('PAVO')
    ) || posOptions[0];

    setIsProcessingPayment(true);
    try {
      if (!pavoSignalRService.isConnected) {
        await pavoSignalRService.connect(companyId, screenData?.branchId || undefined);
      }

      const isGetirOrder = selectedOrder.source === 'getir';
      const isMigrosOrder = selectedOrder.source === 'migros';
      const isYsOrder = selectedOrder.source === 'yemeksepeti';
      const isTrendyolOrder = selectedOrder.source === 'trendyol';
      const isPlatformOrder = isGetirOrder || isMigrosOrder || isYsOrder || isTrendyolOrder;

      // ============================================================
      // AYNI POS AKISINDAKI GIBI TAM FATURA PAYLOAD'U HAZIRLA
      // Platform siparisleri (Getir/Migros/YS/Trendyol): ortak buildInvoiceItems
      //   helper'i — indirim/topping/diff dogrulanmis sekilde uretilir.
      //   Backend'e tum kalemler (negatif indirim dahil) gonderilir.
      // Pavo cihazina ise SADECE pozitif kalemler gonderilir; cihaz tarafi
      //   request.amount (= selectedOrder.totalAmount, indirimli) ile items
      //   toplami farkini sale-level Sale.PriceEffect olarak handle eder.
      // ============================================================
      let invoicePayload: any;
      let invoiceItems: any[] = [];
      let phoneOrderData: any = null;

      if (isPlatformOrder) {
        invoiceItems = buildInvoiceItems(selectedOrder, {
          getirOrders, migrosOrders, yemeksepetiOrders, trendyolOrders,
        }).map((it: any) => ({
          ...it,
          SelectedOptions: it.SelectedOptions ?? null,
          TotalPriceDifference: it.TotalPriceDifference ?? 0,
        }));
      } else {
        // Paket (telefon) siparisi: backend'den getPackageOrderById ile gelir
        // (recipe/menu islemleri icin bu yol korunur — buildInvoiceItems kapsaminda degil).
        const orderDetails = await getPackageOrderById(selectedOrderId);
        if (!orderDetails.Success || !orderDetails.Data) {
          throw new Error('Siparis detaylari alinamadi');
        }
        phoneOrderData = orderDetails.Data;
        invoiceItems = (phoneOrderData.Items || []).map((item: any) => ({
          ProductId: item.ProductId || item.InventoryItemId || ZERO_GUID,
          ProductName: item.ProductName || item.Name,
          WarehouseId: item.WarehouseId || screenData?.defaultWarehouseId || ZERO_GUID,
          Quantity: item.Quantity || 1,
          UnitPrice: item.UnitPrice || 0,
          VatRate: item.TaxRate || item.VatRate || 0,
          SctRate: item.SctRate || 0,
          PriceType: item.PriceType || 'VAT_INCLUDED',
          HasRecipe: item.HasRecipe || false,
          HasMenu: item.HasMenu || false,
          SelectedOptions: item.SelectedOptions || null,
          TotalPriceDifference: item.TotalPriceDifference || 0,
        }));
      }

      const wh = await resolveWarehouseForBranch(companyId, screenData?.branchId, screenData?.defaultWarehouseId, allWarehouses);
      invoiceItems = await enrichItemsWithInventory(invoiceItems, companyId, wh, platformResolveContext(selectedOrder, screenData));

      // Platform-spesifik invoicePayload ust seviye alanlari
      const orderTotal = selectedOrder.totalAmount;
      const baseInvoiceFields = {
        CompanyName: companyNameCache || null,
        ScreenId: screenData?.id || null,
        BankAccountId: parseInt(pavoPos.bankAccountId),
        BankSubAccountId: pavoPos.subAccountId ? parseInt(pavoPos.subAccountId) : null,
        BankAccountName: pavoPos.bankAccountName || pavoPos.label,
        TotalAmount: orderTotal, Currency: 'TRY',
        CreatedBy: currentCashier?.cashierName || currentUserEmail || 'PavoPay',
        BranchId: safeParseBranchId(screenData?.branchId), BranchName: null,
        IsRestaurantMode: false,
        IsPackageMode: false, PackageOrderId: null,
        IsGetirMode: false, GetirOrderId: null,
        IsMigrosMode: false, MigrosOrderId: null,
        IsYemeksepetiMode: false, YemeksepetiOrderId: null,
        IsTrendyolMode: false, TrendyolPackageId: null,
        SkipStockMovement: false,
        CashierId: currentCashier?.cashierId || null,
        CashierName: currentCashier?.cashierName || 'PavoPay',
        AuthCode: null, BatchNo: null, MaskedCardNumber: null, FiscalTransactionId: null, CardType: null,
      };

      if (isGetirOrder) {
        invoicePayload = {
          ...baseInvoiceFields,
          AccountId: 1,
          AccountName: `GETİR - ${selectedOrder.customerName || 'Müşteri'}`,
          Description: `Getir Sipariş Ödemesi (PavoPay) - ${selectedOrder.shortOrderNo}`,
          IsGetirMode: true, GetirOrderId: selectedOrderId,
          Items: invoiceItems,
        };
      } else if (isMigrosOrder) {
        invoicePayload = {
          ...baseInvoiceFields,
          AccountId: 1,
          AccountName: `MIGROS - ${selectedOrder.customerName || 'Musteri'}`,
          Description: `Migros Siparis Odemesi (PavoPay) - ${selectedOrder.shortOrderNo}`,
          IsMigrosMode: true, MigrosOrderId: selectedOrderId,
          CustomerPhone: selectedOrder.customerPhone || '',
          CustomerAddress: selectedOrder.customerAddress || '',
          Items: invoiceItems,
        };
      } else if (isYsOrder) {
        invoicePayload = {
          ...baseInvoiceFields,
          AccountId: 1,
          AccountName: `Y.SEPETİ - ${selectedOrder.customerName || 'Musteri'}`,
          Description: `Yemeksepeti Siparis Odemesi (PavoPay) - ${selectedOrder.shortOrderNo}`,
          IsYemeksepetiMode: true, YemeksepetiOrderId: selectedOrderId,
          CustomerPhone: selectedOrder.customerPhone || '',
          CustomerAddress: selectedOrder.customerAddress || '',
          Items: invoiceItems,
        };
      } else if (isTrendyolOrder) {
        invoicePayload = {
          ...baseInvoiceFields,
          AccountId: 1,
          AccountName: `TRENDYOL - ${selectedOrder.customerName || 'Müşteri'}`,
          Description: `Uber Eats Sipariş Ödemesi (PavoPay) - ${selectedOrder.shortOrderNo}`,
          IsTrendyolMode: true, TrendyolPackageId: selectedOrder.trendyolPackageId || null,
          CustomerPhone: selectedOrder.customerPhone || '',
          CustomerAddress: selectedOrder.customerAddress || '',
          Items: invoiceItems,
        };
      } else {
        // Paket (telefon) siparisi
        invoicePayload = {
          ...baseInvoiceFields,
          AccountId: phoneOrderData?.AccountId,
          AccountName: phoneOrderData?.AccountName || selectedOrder.customerName,
          Description: `Paket Siparis Odemesi (PavoPay) - ${selectedOrder.shortOrderNo}`,
          IsPackageMode: true, PackageOrderId: selectedOrderId,
          Items: invoiceItems,
        };
      }

      // Pavo cihazina SADECE POZITIF kalemler gonderilir.
      // Negatif indirim kalemleri sadece backend faturasina yazilir;
      // PavoPay tarafi sale-level indirimi cihazda Sale.PriceEffect ile handle eder.
      // Boylece PavoPay validasyonu (item kalemleri toplami == GrossPrice) bozulmaz
      // ve fis uzerinde "Genel Indirim %X" tek satir olarak basilir.
      //
      // ⚠️ Kesirli adet (1.5, 0.5 vb.) yönetimi:
      //   Pavo cihaz tarafı (gts_pavo / PavoPay intent) `Quantity` alanını
      //   integer olarak işliyor — 1.5 değeri cihazda 1'e cast oluyor ama
      //   bizim `amount` (= qty × unitPrice) doğru tutar. PavoPay
      //   validasyonu `quantity * unitPrice == grossPrice` kuralını
      //   çiğniyor ve hata kodu 69 ("Sipariş kalemi miktarı ve birim ücreti
      //   hesabı(190.00) sipariş kalemi brüt(285.0) ücrete eşit olmalıdır")
      //   alıyoruz. Çözüm: Kesirli kalemleri `quantity=1` + `price=amount`
      //   olarak yeniden modelle. Toplam tutar bozulmaz, fiş üzerindeki
      //   ad'a kesirli adet suffix olarak yansır ("KAŞARLI PİDE (×1.5)").
      const pavoDeviceItems = (invoicePayload.Items || [])
        .filter((it: any) => Number(it.UnitPrice || 0) >= 0)
        .map((it: any) => {
          const rawQty = Number(it.Quantity || 1);
          // Pavo KDV dahil fiyat bekler. KDV hariç kalem (stok kartında fiyatı
          // KDV hariç girilmiş ürün) ham fiyatla giderse kalemler toplamı
          // sipariş toplamından düşük kalır, cihaz ödemeyi reddeder.
          const unitPrice = Number(it.UnitPrice || 0);
          const rawPrice = it.PriceType === 'VAT_EXCLUDED'
            ? Number((unitPrice * (1 + Number(it.VatRate || 0) / 100)).toFixed(2))
            : unitPrice;
          const amount = Number((rawPrice * rawQty).toFixed(2));
          const isFractionalQty = !Number.isInteger(rawQty);
          const finalQuantity = isFractionalQty ? 1 : rawQty;
          const finalPrice = isFractionalQty ? amount : rawPrice;
          const baseName = it.ProductName || 'Urun';
          const finalName = isFractionalQty
            ? `${baseName} (×${Number(rawQty.toFixed(3)).toString()})`
            : baseName;
          return {
            name: finalName,
            price: finalPrice,
            quantity: finalQuantity,
            amount,
          };
        });

      const paymentRequest = await pavoApiService.createPaymentRequest(
        companyId,
        screenData?.branchId || '',
        pavoDeviceCode,
        selectedOrder.totalAmount,
        pavoDeviceItems,
        'package_order',
        selectedOrder.shortOrderNo || String(selectedOrder.id),
        undefined,
        {
          orderSource: selectedOrder.source,
          orderId: isTrendyolOrder
            ? (selectedOrder.trendyolPackageId ?? String(selectedOrderId))
            : String(isMigrosOrder ? (selectedOrder.migrosOrderId ?? selectedOrderId) : selectedOrderId),
          screenId: screenData?.id ? parseInt(String(screenData.id)) : undefined,
          accountId: invoicePayload.AccountId,
          accountName: invoicePayload.AccountName,
          // ✅ Tam fatura payload'u - server bu payload'u direkt process-pos-with-invoice'a postlar
          invoicePayload,
        },
      );
      const requestId = paymentRequest.requestId;

      // ✅ Ödeme niyeti: sunucunun Pavo işi bu ödemeyi `pavo_{RequestId}` anahtarıyla
      // InvoicePayloadJson'dan zaten kaydeder; niyet AYNI anahtarla yazılır (mükerrer
      // satış oluşmaz), sunucu sonucu PavoRequestId ile cihazdan da öğrenir.
      // Cihaza (SignalR) gönderimden ÖNCE yazılır.
      const pavoIntentKey: string | null = requestId ? `pavo_${requestId}` : null;
      const pavoIntentPayload: any = pavoIntentKey ? { ...invoicePayload, IdempotencyKey: pavoIntentKey } : null;
      if (pavoIntentPayload) {
        await openOrderPaymentIntent({
          request: pavoIntentPayload,
          endpoint: 'process-pos-with-invoice',
          paymentKind: 'pos',
          source: selectedOrder.source,
          screenData,
          cashierName: currentCashier?.cashierName,
          reportNoDeviceResult: false,
          fiscalDevice: 'pavo_p2p',
          deviceCode: pavoDeviceCode,
          pavoRequestId: requestId,
        });
      }
      try {
        await pavoSignalRService.sendPaymentRequest(pavoDeviceCode, {
          RequestId: requestId,
          TotalAmount: selectedOrder.totalAmount,
          Items: pavoDeviceItems,
          SourceType: 'package_order',
          SourceReference: selectedOrder.shortOrderNo || String(selectedOrder.id),
          OrderSource: selectedOrder.source,
        });
      } catch (_) {}

      // Pavo'ya istek gonderildi -> modali hemen kapat, butonlari birak
      // Sonuc arka planda SignalR callback'leri ile dinlenecek
      setIsPaymentModalOpen(false);
      setIsProcessingPayment(false);
      setSnackbar({ open: true, message: 'Ödeme Pavo\'ya gönderildi. Cihazda onay bekleniyor...', severity: 'info' });

      // Aktif odemeler listesini hemen yenilemek icin hafif bir reload
      loadPackageOrders();

      // Arka planda sonucu dinle (fire-and-forget)
      const callbackId = `pavo_pkg_${requestId}`;
      let resolved = false;
      const cleanup = () => { pavoSignalRService.removeCallbacks(callbackId); clearTimeout(tid); };
      // 10 dakika sonra sessizce kaldir (kullanici aktif odemeler modalinden takip/iptal edebilir)
      const tid = setTimeout(() => { if (!resolved) { resolved = true; cleanup(); } }, 600000);
      pavoSignalRService.onPaymentCompleted(callbackId, async (rid) => {
        if (rid !== requestId || resolved) return;
        resolved = true; cleanup();
        // Cihaz onayladı: niyete son yükü bildir (sunucu satışı aynı anahtarla tamamlar)
        if (pavoIntentKey && pavoIntentPayload) {
          void reportDeviceResult(pavoIntentKey, pavoIntentPayload, {
            fiscalResult: 'onaylandi',
            endpoint: 'process-pos-with-invoice',
            paymentKind: 'pos',
          });
        }
        setSnackbar({ open: true, message: 'PavoPay P2P ödeme başarılı! Fatura sunucu tarafında oluşturuluyor.', severity: 'success' });
        await new Promise(r => setTimeout(r, 1500));
        try { await loadPackageOrders(); } catch (_) {}
      });
      pavoSignalRService.onPaymentFailed(callbackId, (rid, _, msg) => {
        if (rid !== requestId || resolved) return;
        resolved = true; cleanup();
        // Cihaz reddetti: para alınmadı → niyet terk edilir
        abandonPaymentIntent(pavoIntentKey, `Pavo cihazı reddetti: ${msg || 'başarısız'}`);
        setSnackbar({ open: true, message: msg || 'Pavo ödemesi başarısız', severity: 'error' });
      });
      pavoSignalRService.onPaymentCancelled(callbackId, async (rid) => {
        if (rid !== requestId || resolved) return;
        resolved = true; cleanup();
        // Cihazda iptal: para alınmadı → niyet terk edilir
        abandonPaymentIntent(pavoIntentKey, 'Pavo cihazında iptal edildi');
        setSnackbar({ open: true, message: 'Pavo ödemesi iptal edildi', severity: 'warning' });
        try { await loadPackageOrders(); } catch (_) {}
      });
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'PavoPay P2P hatası', severity: 'error' });
      setIsProcessingPayment(false);
    }
  }, [selectedOrder, selectedOrderId, companyId, screenData, currentCashier, setSnackbar, setIsProcessingPayment, setIsPaymentModalOpen, loadPackageOrders]);

  // ════════════════════════════════════════════════════════════════
  // REST-P2P ONLINE FIS YAZDIRMA AKISI — Harici Odeme (ExternalPayments)
  //
  // Amac: Yemeksepeti / Trendyol / Migros / Getir gibi platform tarafindan
  //   ONLINE odenmis siparislerin PavoPay'den mali fis kesimi.
  //
  // Mimari: Cihaz tarafi `paymentMode='external'` ile alir; PavoPay'e
  //   `Sale.ExternalPayments[]` (Mediator=14, Type=22, Brand=200..203) ile
  //   completeSale eder. Kasiyer ek tahsilat YAPMAZ; fiste "HARICI ODEME"
  //   basligi altinda platform adi ve net tutar gorunur.
  //
  // Pavo P2P (siparis tahsilati) akisindan TAMAMEN AYRIDIR:
  //   • Pavo P2P → pavo_order modu cihazlar (handlePavoP2pPayment)
  //   • REST-P2P → pavo_rest_p2p modu cihazlar (handleRestP2pOnlineReceipt)
  //   Odeme alma modali "PavoPay P2P" sadece pavo_order'i listeler; siparis
  //   detay modalindaki "Online Fis Yazdir" sadece pavo_rest_p2p'yi listeler.
  //
  // PavoPay Payment Provider Brand mapping (yaml satir 858-862):
  //   200 = Yemek Sepeti Online Odeme
  //   201 = Getir Online Odeme
  //   202 = Trendyol Online Odeme
  //   203 = Migros Yemek Online Odeme
  //   204 = Web Sitesi Online Odeme  (sonraki faz; bu turda devre disi)
  // ════════════════════════════════════════════════════════════════
  // Sadece bu 4 kaynak bu fazda destekli. Brand bulunmazsa erken cikilir.
  const PAVO_BRAND_BY_SOURCE: Record<string, { brand: number; label: string }> = {
    yemeksepeti: { brand: 200, label: 'Yemek Sepeti Online Ödeme' },
    getir:       { brand: 201, label: 'Getir Online Ödeme' },
    trendyol:    { brand: 202, label: 'Trendyol Online Ödeme' },
    migros:      { brand: 203, label: 'Migros Yemek Online Ödeme' },
    // 'web' / 'phone' / 'qr' — sonraki faz, bu turda destek yok.
  };
  // ✅ C1 fix: restP2pReceiptSelectOpen state PackageOrderScreen tarafında tutuluyor
  // (anyOtherModalOpen useMemo'sundan görünebilsin diye). Setter dışarıdan geliyor.
  const [restP2pReceiptOrder, setRestP2pReceiptOrder] = useState<PackageOrder | null>(null);

  const openRestP2pReceiptSelect = useCallback((order: PackageOrder | null) => {
    console.group('[OnlineReceipt] openRestP2pReceiptSelect');
    if (!order) {
      console.error('order=null cagri reddedildi');
      console.groupEnd();
      setSnackbar({ open: true, message: 'Sipariş bilgisi alınamadı', severity: 'error' });
      return;
    }
    console.log('REST-P2P cihaz secim modali aciliyor — order ozet:', {
      id: order.id,
      source: order.source,
      shortOrderNo: order.shortOrderNo,
      totalAmount: order.totalAmount,
      paymentStatus: (order as any).paymentStatus,
      paymentMethod: (order as any).paymentMethod,
    });
    console.groupEnd();
    setRestP2pReceiptOrder(order);
    setRestP2pReceiptSelectOpen(true);
  }, [setSnackbar, setRestP2pReceiptSelectOpen]);

  const handleRestP2pOnlineReceipt = useCallback(async (pavoDeviceCode: string) => {
    console.group('[OnlineReceipt] handleRestP2pOnlineReceipt');
    console.log('Cagri parametreleri:', {
      pavoDeviceCode,
      hasOrder: !!restP2pReceiptOrder,
      orderSnapshot: restP2pReceiptOrder ? {
        id: restP2pReceiptOrder.id,
        source: restP2pReceiptOrder.source,
        shortOrderNo: restP2pReceiptOrder.shortOrderNo,
        totalAmount: restP2pReceiptOrder.totalAmount,
      } : null,
      companyId,
    });
    if (!pavoDeviceCode) {
      console.error('pavoDeviceCode bos -> early return');
      console.groupEnd();
      setSnackbar({ open: true, message: 'Lütfen bir REST-P2P cihazı seçin', severity: 'error' });
      return;
    }
    if (!restP2pReceiptOrder || !companyId) {
      console.error('order veya companyId eksik -> early return', {
        hasOrder: !!restP2pReceiptOrder, hasCompanyId: !!companyId,
      });
      console.groupEnd();
      setSnackbar({ open: true, message: 'Sipariş veya şirket bilgisi eksik', severity: 'error' });
      return;
    }
    const order = restP2pReceiptOrder;
    const totalAmount = Number((order.totalAmount || 0).toFixed(2));
    if (totalAmount <= 0) {
      console.error('totalAmount <= 0 -> early return', { totalAmount });
      console.groupEnd();
      setSnackbar({ open: true, message: 'Sipariş tutarı sıfır - fiş kesilemez', severity: 'error' });
      return;
    }

    // Brand mapping: bu fazda sadece 4 platform destekli (yemeksepeti / getir
    // / trendyol / migros). Diger kaynaklarda (phone / web / qr) buton zaten
    // gorunse bile erken cikis ile uyari verilir.
    const sourceKey = (order.source || '').toLowerCase();
    const brandInfo = PAVO_BRAND_BY_SOURCE[sourceKey];
    console.log('Brand mapping:', {
      sourceKey,
      brandInfo: brandInfo || 'YOK (desteklenmeyen kaynak)',
    });
    if (!brandInfo) {
      console.warn('Brand bulunamadi -> early return');
      console.groupEnd();
      setSnackbar({
        open: true,
        message: 'Bu kaynaktan gelen siparişler için online fiş şu an desteklenmiyor.',
        severity: 'warning',
      });
      return;
    }

    setIsProcessingPayment(true);
    try {
      // Platform sipariş ürünlerini ve indirim bilgisini topla → cihaz tarafina
      // PavoPay AddedSaleItems olarak yansir (orijinal birim fiyat, indirim
      // ayri bir Sale.PriceEffect Type=2 alaninda).
      // Pavo cihazindaki resolvedSaleItems alanlari: Name, Quantity, UnitPrice,
      // VatRate, Total/GrossTotal/NetTotal — payload schemasiyla bire bir.
      let rawInvoiceItems: any[] = [];
      try {
        rawInvoiceItems = buildInvoiceItems(order, {
          getirOrders, migrosOrders, yemeksepetiOrders, trendyolOrders,
        }) || [];
      } catch (e) {
        console.warn('[OnlineReceipt] buildInvoiceItems hata:', e);
      }
      console.log('[OnlineReceipt] buildInvoiceItems sonuc:', {
        count: rawInvoiceItems.length,
        items: rawInvoiceItems.map((it: any) => ({
          name: it?.ProductName, qty: it?.Quantity, unit: it?.UnitPrice,
        })),
      });

      // gts_pavo `resolvedSaleItems` getter'i Name + Quantity + UnitPrice
      // alanlarina bakar. PascalCase olan ProductName -> Name'a cevirip
      // payload'i cihaz tarafinin bekledigi formata sokariz. Negatif
      // UnitPrice'lar (Platform Indirimi) toplanip ayri PriceEffect (Type=2)
      // olarak gonderilir; positif item'lar AddedSaleItems'a gider.
      //
      // 🧾 KDV — ONLINE FIS (yemek/restoran) icin %10 SABIT:
      //   buildInvoiceItems platform sipariş ürünlerine VatRate=0 atiyor cunku
      //   Yemeksepeti/Trendyol/Migros/Getir API'lari urun bazinda KDV bilgisi
      //   vermiyor. Pavo'da `VatRate=0` gondermek `TaxGroupCode=KDV0` ile
      //   esiyor ve fis "KDV: 0,00" basiyor — bu yanlis. Yemek sektoru hizmet
      //   bedeli %10 oldugundan, online fis kesiminde butun urunlere KDV %10
      //   uygulanir. Eger ileride buildInvoiceItems gercek KDV oranini
      //   doldurursa (>0), o oran korunur.
      const ONLINE_RECEIPT_DEFAULT_VAT = 10;
      const positiveItems: any[] = [];
      let discountSum = 0;
      for (const it of rawInvoiceItems) {
        const qty = Number(it?.Quantity || 1);
        const unit = Number(it?.UnitPrice || 0);
        const total = Math.round(qty * unit * 100) / 100;
        if (unit < 0) {
          // Negatif kalem = indirim
          discountSum += Math.abs(total);
          continue;
        }
        if (total <= 0) continue;
        const itemVat = Number(it?.VatRate || 0);
        // Pavo Quantity integer constraint'i — kesirli adetlerde Pavo
        // `qty * unitPrice == grossTotal` doğrulaması bozuluyor (hata 69).
        // Quantity=1 + UnitPrice=total kullanıyoruz; ad'a "(×1.5)" suffix.
        const isFractionalQty = !Number.isInteger(qty);
        const pavoQuantity = isFractionalQty ? 1 : qty;
        const pavoUnit = isFractionalQty ? total : unit;
        const baseName = it?.ProductName || 'Ürün';
        const pavoName = isFractionalQty
          ? `${baseName} (×${Number(qty.toFixed(3)).toString()})`
          : baseName;
        positiveItems.push({
          Name: pavoName,
          Quantity: pavoQuantity,
          UnitPrice: pavoUnit,
          Total: total,
          VatRate: itemVat > 0 ? itemVat : ONLINE_RECEIPT_DEFAULT_VAT,
        });
      }
      const itemsTotal = positiveItems.reduce(
        (s, it) => s + (Number(it.Total) || 0), 0,
      );
      // Tutarsizlik dengesi: itemsTotal - discount = totalAmount olmali.
      // Eger fark varsa son bir indirim/duzeltme satiri ekleyelim ki Pavo
      // hesabi tutsun (yoksa hata kodu 69 paymentInformationAmountMisMatch).
      const expected = Math.round((itemsTotal - discountSum) * 100) / 100;
      const totalRounded = Math.round(totalAmount * 100) / 100;
      if (positiveItems.length > 0 && Math.abs(expected - totalRounded) > 0.01) {
        const fix = Math.round((expected - totalRounded) * 100) / 100;
        console.warn('[OnlineReceipt] Tutar uyusmazligi - duzeltme indirimi ekleniyor:', {
          itemsTotal, discountSum, expected, totalRounded, fix,
        });
        if (fix > 0) discountSum += fix;
      }

      // Items hic yoksa (fallback) tek satir ozet — eski davranis ama yine
      // de Pavo'da indirim kalemi gozuksun istiyorsak grossPrice icin sade
      // bir orijinal-fiyat satiri ekleyelim. KDV %10 uygulanir (yemek hizmeti).
      if (positiveItems.length === 0) {
        const grossEstimate = totalAmount + discountSum;
        positiveItems.push({
          Name: `${brandInfo.label} - ${order.shortOrderNo || order.id}`,
          Quantity: 1,
          UnitPrice: grossEstimate,
          Total: grossEstimate,
          VatRate: ONLINE_RECEIPT_DEFAULT_VAT,
        });
      }

      const sourceLabel = brandInfo.label; // PavoPay tablosundaki resmi ad
      const externalPayment: RestP2PExternalPayment = {
        type: 22,        // PaymentType "Diger" (Mediator 14'un type'i)
        mediator: 14,    // "Harici Odeme Saglayici"
        brand: brandInfo.brand,
        amount: totalAmount,
        externalReferenceText: order.shortOrderNo || String(order.id || ''),
      };

      const request: RestP2PPaymentRequest = {
        deviceCode: pavoDeviceCode,
        paymentMode: 'external',
        totalAmount,
        // parts UI/log icin tasiyici; cihaz `external` modunda Parts icindeki
        // method'u dikkate almaz (mediatorId=null + externalPayment yolu).
        parts: [{
          method: 'cash',
          amount: totalAmount,
          label: `Online Ödendi - ${sourceLabel}`,
          order: 1,
        }],
        receiptItemsJson: JSON.stringify(positiveItems),
        companyId,
        branchId: screenData?.branchId || null,
        screenId: screenData?.id || null,
        cashierName: currentCashier?.cashierName || currentUserEmail || 'Online Receipt',
        sourceLabel: `${sourceLabel} - ${order.shortOrderNo || order.id}`,
        // Online fiş için Pavo'da mutfak ust/alt bilgisi (Sirket adi + siparis
        // no + AQUASOFT) BASILMASIN — kullanici sadece online ödemeye özel sade
        // fiş istiyor. Bu nedenle kitchenOrderCode ve receiptCompanyName
        // BILEREK null gonderiliyor; gts_pavo TopPrintableItems eklemeyecek.
        kitchenOrderCode: null,
        receiptCompanyName: null,
        externalPayment,
      };

      console.log('[OnlineReceipt] Pavo cihazina REST-P2P istegi gonderiliyor:', {
        deviceCode: pavoDeviceCode,
        paymentMode: request.paymentMode,
        totalAmount,
        externalPayment,
        receiptItemsCount: positiveItems.length,
        discountSum,
        itemsTotal,
        sourceLabel: request.sourceLabel,
      });

      setSnackbar({
        open: true,
        message: `Pavo cihazına online fiş isteği gönderildi (${sourceLabel} #${order.shortOrderNo || order.id})...`,
        severity: 'info',
      });

      const outcome = await restP2PPaymentService.requestPayment(request, {
        timeoutMs: 180000,
      });
      console.log('[OnlineReceipt] REST-P2P sonucu:', outcome);

      if (outcome.status === 'completed') {
        const sn = (outcome.result as any)?.pavoSaleNumber || '';
        console.log('[OnlineReceipt] BASARILI — Pavo SaleNumber:', sn);
        console.groupEnd();
        setSnackbar({
          open: true,
          message: `Online fiş başarıyla kesildi${sn ? ` (Pavo: ${sn})` : ''}`,
          severity: 'success',
        });
        try { await loadPackageOrders(); } catch (_) {}
      } else if (outcome.status === 'cancelled') {
        console.warn('[OnlineReceipt] IPTAL', outcome);
        console.groupEnd();
        setSnackbar({
          open: true,
          message: `Online fiş isteği iptal edildi: ${outcome.reason}`,
          severity: 'warning',
        });
      } else {
        console.error('[OnlineReceipt] BASARISIZ', outcome);
        console.groupEnd();
        setSnackbar({
          open: true,
          message: `Online fiş kesilemedi: ${outcome.message || outcome.errorCode}`,
          severity: 'error',
        });
      }
    } catch (error: any) {
      console.error('[OnlineReceipt] Try-catch beklenmeyen hata:', error);
      try { console.groupEnd(); } catch (_) {}
      setSnackbar({
        open: true,
        message: error?.message || 'Online fiş yazdırma hatası',
        severity: 'error',
      });
    } finally {
      setIsProcessingPayment(false);
      setRestP2pReceiptOrder(null);
    }
  }, [
    restP2pReceiptOrder, companyId, screenData, currentCashier, currentUserEmail,
    companyNameCache, getirOrders, migrosOrders, yemeksepetiOrders, trendyolOrders,
    setSnackbar, setIsProcessingPayment, loadPackageOrders,
  ]);

  return {
    buildCashPaymentRequest,
    handleOpenPaymentModal, handleCashPayment, handlePosPayment, handleOtherPayment, handleMixedPayment,
    handleOnlinePaymentRecord, handleOnlinePaymentFromModal,
    // ✅ C1 fix: pavoP2pSelectOpen / restP2pReceiptSelectOpen state'leri artık
    // PackageOrderScreen tarafında tutuluyor (anyOtherModalOpen'dan görünebilsin).
    handlePavoP2pPayment, openPavoP2pSelect,
    // REST-P2P online fiş yazdırma (yeni)
    openRestP2pReceiptSelect, handleRestP2pOnlineReceipt,
    restP2pReceiptOrder,
  };
}
